import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import {
  Box, Typography, Paper, Card, CardContent, Chip, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Checkbox, FormControlLabel, Tooltip, Alert,
  Divider, CircularProgress, useTheme,
  Accordion, AccordionSummary, AccordionDetails,
  Select, MenuItem, FormControl, Autocomplete, InputLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Apartment as ApartmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon,
  AutoAwesome as BuildIcon,
  Apartment as PropertiesIcon,
  Business as BusinessOwnerIcon,
  Person as PersonOwnerIcon,
  Map as MapIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import AddressFinder, { AddressMatch } from '../components/AddressFinder';
import PropertiesMapDialog, { PropertyMapMarker } from '../components/PropertiesMapDialog';

// Same expand/collapse motion values as NewCertificatesDashboard.tsx's PropertyCard — this page
// deliberately mirrors that component's tree-with-tabs style (per direct request), so the two
// should stay visually/behaviorally identical rather than drift into two different patterns.
const CONTENT_VARIANTS = {
  collapsed: { height: 0, opacity: 0 },
  expanded: { height: 'auto', opacity: 1 },
};

// Short display label for a flat — just "Flat 1"/"Unit 2"/etc. or the literal "Whole Property",
// never the full remainder of the address (street/city/postcode) that's already shown on the
// building's own header line.
const flatShortLabel = (flat: { flat_address: string; normalized_address: string }) => {
  if (flat.flat_address === 'Whole Property') return 'Whole Property';
  const unitMatch = flat.flat_address.match(/\b(?:Flat|Unit|Apt|Apartment|Office)\s*[\w-]+/i);
  if (unitMatch) return unitMatch[0];
  return flat.flat_address !== flat.normalized_address
    ? flat.flat_address.replace(flat.normalized_address, '').replace(/^[,\s]+/, '') || flat.flat_address
    : flat.flat_address;
};

// Sort flats for tab order: "Whole Property" first if present, then ascending by any "Flat N"
// number found in the address, falling back to alphabetical — mirrors the certificates
// dashboard's sortUnitKeys ordering (Whole Property first, numeric ascending) so both trees feel
// the same, adapted for addresses (which carry other digits like a zipcode a blind digit-strip
// would wrongly sort by).
const sortFlats = (flats: FlatRecord[]): FlatRecord[] => {
  const wholeProperty = flats.filter(f => f.flat_address === 'Whole Property');
  const rest = flats.filter(f => f.flat_address !== 'Whole Property').sort((a, b) => {
    const na = a.flat_address.match(/(?:Flat|Office)\s*(\d+)/i);
    const nb = b.flat_address.match(/(?:Flat|Office)\s*(\d+)/i);
    if (na && nb) return parseInt(na[1], 10) - parseInt(nb[1], 10);
    return flatShortLabel(a).localeCompare(flatShortLabel(b));
  });
  return [...wholeProperty, ...rest];
};

// ── types ─────────────────────────────────────────────────────────────────────

type UnitStatus = 'Vacant' | 'Occupied' | 'Notice given' | 'Under renovation' | 'Not lettable';

// Must match backend/models.py's UNIT_STATUSES (DB-enforced via a CHECK constraint).
const UNIT_STATUSES: UnitStatus[] = ['Vacant', 'Occupied', 'Notice given', 'Under renovation', 'Not lettable'];

// Occupied = good (success green); Notice given = a heads-up before it goes vacant (warning
// amber); Under renovation = temporary/informational (teal); Not lettable = a real problem
// state (error red); Vacant = neutral, neither good nor bad on its own (muted grey).
const unitStatusColor = (status: UnitStatus, tokens: Record<string, string>): string => {
  switch (status) {
    case 'Occupied': return tokens.success;
    case 'Notice given': return tokens.warning;
    case 'Under renovation': return tokens.accentTeal;
    case 'Not lettable': return tokens.error;
    default: return tokens.muted; // Vacant
  }
};

interface FlatRecord {
  id: string;
  normalized_address: string;
  flat_address: string;
  monthly_rent: string;
  country: string | null;
  district: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  unit_status: UnitStatus;
  // House in Multiple Occupation — persists on the flat itself (not the tenancy), since HMO
  // status is a property characteristic that doesn't change lease to lease. When true, the
  // Tenancy dialog asks for each tenant's own rent share instead of one lump rent_amount.
  is_hmo: boolean;
  // Geocoding from the address finder (LocationIQ, UK+Cyprus) — see AddressFinder.tsx.
  // address_verified is server-computed (latitude/longitude both set), never client-sent.
  latitude: number | null;
  longitude: number | null;
  formatted_address: string | null;
  address_verified: boolean;
  // Names of tenants on this flat's current Active tenancy, if any — server-computed so the
  // card can show who's actually there without a per-flat tenancy fetch just to render a summary.
  active_tenant_names: string[];
  owner_id: string | null;
  // Who manages this flat day to day — independent of owner_id (an owner who can't manage
  // their own property, e.g. Dad, still owns it; someone else is set here to run it).
  managed_by_id: string | null;
  // Derived server-side from managed_by_id vs owner_id — true iff a manager is set and differs
  // from the owner. See Owner.is_owner/.is_manager below.
  is_third_party_managed: boolean;
  management_fee_type: 'percent' | 'fixed' | null;
  management_fee_value: string | null;
  tenant_name: string | null;
  tenant_email: string | null;
  tenant_phone: string | null;
  payment_reference: string | null;
  deposit_amount: string | null;
  deposit_lodged: boolean;
  rent_due_day: number | null;
}

interface PropertyGroup {
  normalized_address: string;
  flat_count: number;
  owner_id: string | null;
  owner_mixed: boolean;
  managed_by_id: string | null;
  managed_by_mixed: boolean;
  flats: FlatRecord[];
}

interface Owner {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  entity_type: 'individual' | 'business';
  // Which role(s) this owner is eligible for when assigning a property — see the model comment
  // in backend/models.py. A record with both false is rejected by the API.
  is_owner: boolean;
  is_manager: boolean;
  country: string | null;
  vat_number: string | null;
  created_at: string | null;
}

interface Tenant {
  id: string;
  name: string;
  id_reference: string | null;
  phone: string | null;
  email: string | null;
  preferred_contact_channel: 'email' | 'phone' | 'sms' | 'whatsapp' | null;
  preferred_language: string | null;
}

type TenancyStatus = 'Draft' | 'Active' | 'Ended' | 'Terminated';

// A tenant as attached to one specific tenancy — carries its own rent_amount (the tenant's HMO
// room-rent share, only set when the unit is_hmo), distinct from the reusable Tenant record.
interface TenancyTenantEntry extends Tenant {
  rent_amount: string | null;
}

interface Tenancy {
  id: string;
  property_rent_id: string;
  start_date: string;
  end_date: string | null;
  rent_amount: string;
  currency: string;
  payment_frequency: 'monthly' | 'weekly' | 'fortnightly' | 'quarterly' | 'annually';
  payment_due_day: number | null;
  deposit_amount: string | null;
  deposit_status: 'held' | 'returned' | 'partially_returned' | null;
  notice_period_days: number | null;
  notice_served_date: string | null;
  status: TenancyStatus;
  display_status: string;   // status, or 'Expiring' when within the threshold
  is_expiring_soon: boolean;
  tenants: TenancyTenantEntry[];
  created_at: string | null;
}

// Must match backend/models.py's TENANCY_PAYMENT_FREQUENCIES / TENANCY_DEPOSIT_STATUSES /
// TENANT_CONTACT_CHANNELS.
const TENANCY_PAYMENT_FREQUENCIES: Tenancy['payment_frequency'][] = ['monthly', 'weekly', 'fortnightly', 'quarterly', 'annually'];

// The two markets this app is actually used in (UK/Cyprus) plus USD as the common third option —
// a tenancy's own rent_amount/currency is what the signed agreement says, independent of the
// wider app's still-£-only KPI/summary display (see PropertyRent.country in models.py).
const TENANCY_CURRENCIES = ['GBP', 'EUR', 'USD'];

// Sentinel id for the synthetic "Add new tenant" / "Add "X"" option appended to the tenant
// picker's own dropdown — no real Tenant row ever has this id, so it's always distinguishable
// from an actual selection.
const ADD_NEW_TENANT_OPTION_ID = '__add_new_tenant__';

// A due day is only meaningful up to 28 for every month without exception; 29-31 don't exist in
// every month (Feb, and the 30-day months). Rather than let a free-typed "31" silently mean
// different things in different months, 31 is the one reserved sentinel for "last day of month"
// — no real calendar day is ever *literally* the 31st in a way this app needs to distinguish
// from "end of month", so the convention is unambiguous. Constrained to a Select (not a number
// input) so an invalid day can't be typed in the first place.
const DUE_DAY_END_OF_MONTH = 31;
const DUE_DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => i + 1);
const TENANCY_DEPOSIT_STATUSES: Exclude<Tenancy['deposit_status'], null>[] = ['held', 'returned', 'partially_returned'];
const TENANT_CONTACT_CHANNELS: Exclude<Tenant['preferred_contact_channel'], null>[] = ['email', 'phone', 'sms', 'whatsapp'];

// UK and Cyprus are the two markets this app is actually used in today — one-click presets;
// any other country in the world is still supported via the searchable "Other" field below.
// Used for both a owner's own country and a property's country, which are deliberately
// independent (a Cyprus-based business can own/manage a UK flat).
const COUNTRY_PRESETS = ['United Kingdom', 'Cyprus'];

// Every other country (excluding the two presets above), alphabetical — searched via
// Autocomplete rather than scrolled, since a plain <Select> with ~190 options is unusable.
const WORLD_COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia',
  'Comoros', 'Congo (Congo-Brazzaville)', 'Costa Rica', 'Croatia', 'Cuba', 'Czech Republic',
  'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini',
  'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
  'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya',
  'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives',
  'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
  'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
  'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
  'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
  'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand',
  'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
  'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United States', 'Uruguay', 'Uzbekistan',
  'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

// Shared country picker: preset dropdown (UK/Cyprus/Other), with a searchable Autocomplete of
// every other country that only appears in "Other" mode. `mode` is tracked internally (not
// derivable from `value` alone once it's empty — that's what previously made switching to
// "Other" before picking a country snap back to looking like "not set").
const CountryField: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const { palette } = useTheme();
  const [mode, setMode] = useState<'preset' | 'other'>(value && !COUNTRY_PRESETS.includes(value) ? 'other' : 'preset');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <FormControl size="small" fullWidth>
        <Select
          value={mode === 'other' ? 'Other' : value}
          displayEmpty
          onChange={e => {
            const v = e.target.value;
            if (v === 'Other') { setMode('other'); onChange(''); }
            else { setMode('preset'); onChange(v); }
          }}
          renderValue={(val) => !val
            ? <Typography sx={{ fontSize: '0.9rem', color: palette.tokens.mutedSoft }}>Not set</Typography>
            : (mode === 'other' ? 'Other' : val as string)}
        >
          <MenuItem value=""><em>Not set</em></MenuItem>
          {COUNTRY_PRESETS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          <MenuItem value="Other">Other</MenuItem>
        </Select>
      </FormControl>
      {mode === 'other' && (
        <Autocomplete
          size="small"
          options={WORLD_COUNTRIES}
          value={WORLD_COUNTRIES.includes(value) ? value : null}
          onChange={(_, v) => onChange(v || '')}
          renderInput={(params) => <TextField {...params} placeholder="Search for a country" />}
        />
      )}
    </Box>
  );
};

// A small, fixed set of existing theme tokens — cycled deterministically per owner (by id) so
// each owner keeps a stable color across renders/reloads, purely to make the card grid easier
// to tell apart at a glance. Not a new palette — every value here is already a DESIGN_TOKENS key.
const OWNER_AVATAR_TOKENS = ['primary', 'accentTeal', 'accentAmber', 'success'] as const;
const ownerAvatarToken = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return OWNER_AVATAR_TOKENS[hash % OWNER_AVATAR_TOKENS.length];
};

// The Owners sidebar panel deliberately doesn't grow with content — it would otherwise push
// the welcome hero taller too, since they're flex siblings in the same stretched row. Capped at
// OWNER_PANEL_CAP visible cards, always rendered at one fixed size (OWNER_CARD_SIZES[2] —
// see the panel render below); past the cap a "See more" row replaces the add-card instead of
// the list growing further. Size 1 is only used in the "see more" overflow dialog, which has no
// such space constraint.
const OWNER_PANEL_CAP = 2;
const OWNER_CARD_SIZES = {
  1: { avatar: 40, pad: 1.5, font: '0.9rem', gap: 1.25 },
  2: { avatar: 36, pad: 1.25, font: '0.85rem', gap: 1.1 },
} as const;

const OwnerCard: React.FC<{
  owner: Owner;
  size: keyof typeof OWNER_CARD_SIZES;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ owner: p, size, onEdit, onDelete }) => {
  const { palette } = useTheme();
  const tokenKey = ownerAvatarToken(p.id);
  const sz = OWNER_CARD_SIZES[size];
  return (
    <Card
      elevation={0}
      onClick={onEdit}
      sx={{
        border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, bgcolor: palette.tokens.surfaceCard,
        cursor: 'pointer', transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: palette.tokens.surfaceSoft },
      }}
    >
      <CardContent sx={{ p: sz.pad, '&:last-child': { pb: sz.pad }, display: 'flex', alignItems: 'center', gap: sz.gap }}>
        <Box sx={{
          width: sz.avatar, height: sz.avatar, borderRadius: '50%', flexShrink: 0,
          bgcolor: palette.tokens[tokenKey], color: palette.tokens.onPrimary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {p.entity_type === 'business'
            ? <BusinessOwnerIcon sx={{ fontSize: sz.avatar * 0.5 }} />
            : <PersonOwnerIcon sx={{ fontSize: sz.avatar * 0.5 }} />}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Tooltip title={[
            p.entity_type === 'business' ? 'Business' : 'Individual',
            p.is_owner && p.is_manager ? 'Owner & manager' : p.is_owner ? 'Owner only' : 'Manager only',
            p.country,
            p.vat_number ? `VAT ${p.vat_number}` : null,
            p.email,
            p.phone,
          ].filter(Boolean).join(' · ')}>
            <Typography sx={{ fontWeight: 700, fontSize: sz.font, color: palette.tokens.ink, lineHeight: 1.3, cursor: 'default' }} noWrap>
              {p.name}
            </Typography>
          </Tooltip>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          sx={{ color: palette.tokens.muted, '&:hover': { color: palette.tokens.error }, p: 0.5, flexShrink: 0 }}
        >
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </CardContent>
    </Card>
  );
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtGBP = (val: string | number) =>
  `£${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const EMPTY_FORM = {
  normalized_address: '',
  flat_address: '',
  monthly_rent: '',
  country: '',
  district: '',
  bedrooms: '',
  bathrooms: '',
  unit_status: 'Vacant' as UnitStatus,
  is_hmo: false,
  // Set only via AddressFinder's onSelect — never typed directly. See selectAddressMatch/
  // clearAddressMatch below.
  latitude: null as number | null,
  longitude: null as number | null,
  formatted_address: null as string | null,
  place_id: null as string | null,
  rent_due_day: '',
  tenant_name: '',
  tenant_email: '',
  tenant_phone: '',
  deposit_amount: '',
  deposit_lodged: false,
  rent_effective_from: todayISO(),
  owner_id: '',
  managed_by_id: '',
  management_fee_type: 'percent' as 'percent' | 'fixed',
  management_fee_value: '',
};

// A clickable summary row on the flat detail panel — replaces the old single "Edit" dialog
// covering everything at once (direct feedback: too much in one popup) with one focused entry
// point per concern (Tenancy / Management / Property details), each opening its own small dialog.
const SummaryRow: React.FC<{ label: string; value: string; onClick: () => void }> = ({ label, value, onClick }) => {
  const { palette } = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1, display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1,
        borderRadius: '8px', border: `1px solid ${palette.tokens.hairline}`, cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': { bgcolor: palette.tokens.surfaceSoft, borderColor: palette.tokens.mutedSoft },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: palette.tokens.mutedSoft, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: palette.tokens.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value}
        </Typography>
      </Box>
      <ChevronRightIcon sx={{ fontSize: 16, color: palette.tokens.mutedSoft, flexShrink: 0 }} />
    </Box>
  );
};

// "Whole property" vs "Building with flats/offices" — the first choice on the Add dialog, which
// decides whether the type/number fields below are locked (whole property) or open for the
// user to pick a unit type and number (see propertyKind/selectPropertyKind).
const KindOptionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  selected: boolean;
  onClick: () => void;
}> = ({ icon, title, selected, onClick }) => {
  const { palette } = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, p: 1.5, cursor: 'pointer',
        borderRadius: '10px',
        border: `1.5px solid ${selected ? palette.primary.main : palette.tokens.hairline}`,
        bgcolor: selected ? alpha(palette.primary.main, 0.06) : 'transparent',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': selected ? {} : { borderColor: palette.tokens.mutedSoft, bgcolor: palette.tokens.surfaceSoft },
        color: selected ? palette.primary.main : palette.tokens.ink,
      }}
    >
      {icon}
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{title}</Typography>
    </Box>
  );
};

// Collapsed-by-default grouping for the Add/Edit dialog's less-essential fields (per direct
// feedback that the dialog showed too much at once) — each section expands independently, no
// shared "only one open" state, since there's no reason opening Rent should close Property info.
const FormAccordion: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const { palette } = useTheme();
  return (
    <Accordion
      elevation={0}
      disableGutters
      sx={{
        border: `1px solid ${palette.tokens.hairline}`,
        borderRadius: '8px !important',
        bgcolor: 'transparent',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />} sx={{ minHeight: 44, '&.Mui-expanded': { minHeight: 44 } }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: palette.tokens.muted }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

// One building — mirrors NewCertificatesDashboard.tsx's PropertyCard structure and motion
// exactly (full-width card, icon tile + serif building name, pseudo-tab row switching between
// flats, height-animated content panel) so the Properties tree reads as the same component
// family as the Certificates tree, just showing flat/owner details instead of certificates.
const BuildingCard: React.FC<{
  prop: PropertyGroup;
  owners: Owner[];
  expanded: boolean;
  onToggleExpand: () => void;
  onAssignBuildingOwner: (normalizedAddress: string, ownerId: string | null) => void;
  onAssignBuildingManager: (normalizedAddress: string, managedById: string | null) => void;
  onUpdateStatus: (flatId: string, unitStatus: UnitStatus) => void;
  onRenameBuilding: (oldAddress: string, newAddress: string) => void;
  onOpenTenancy: (flat: FlatRecord) => void;
  onOpenPropertyDetails: (flat: FlatRecord) => void;
  onDeleteFlat: (flat: FlatRecord) => void;
  onAddFlatToBuilding: (normalizedAddress: string) => void;
  onDeleteBuilding: (prop: PropertyGroup) => void;
}> = ({ prop, owners, expanded, onToggleExpand, onAssignBuildingOwner, onAssignBuildingManager, onUpdateStatus, onRenameBuilding, onOpenTenancy, onOpenPropertyDetails, onDeleteFlat, onAddFlatToBuilding, onDeleteBuilding }) => {
  const { palette } = useTheme();
  const sortedFlats = sortFlats(prop.flats);
  const [selectedFlatId, setSelectedFlatId] = useState<string>(sortedFlats[0]?.id);

  // Click-to-edit the building's own address — the one place normalized_address can be
  // changed, since editing a single flat deliberately leaves it alone (see Property details).
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(prop.normalized_address);
  const commitNameEdit = () => {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== prop.normalized_address) {
      onRenameBuilding(prop.normalized_address, trimmed);
    }
    setEditingName(false);
  };

  // Keep selectedFlatId valid if this building's flats change (e.g. one gets deleted).
  useEffect(() => {
    if (!sortedFlats.some(f => f.id === selectedFlatId)) {
      setSelectedFlatId(sortedFlats[0]?.id);
    }
  }, [prop.flats.map(f => f.id).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps


  // Animate height across tab switches, same technique as the certificates dashboard: wrap the
  // tab row + content panel together, freeze the current pixel height on click, then animate to
  // the new content's natural height in a layout effect. Avoids the outer card's own
  // expand/collapse height animation (which only tracks size during its own mount/unmount)
  // visibly lagging behind a child that resizes independently after that transition settles.
  const tabSectionRef = useRef<HTMLDivElement>(null);
  const handleSelectFlat = (flatId: string) => {
    const el = tabSectionRef.current;
    if (el) el.style.height = `${el.scrollHeight}px`;
    setSelectedFlatId(flatId);
  };
  useLayoutEffect(() => {
    const el = tabSectionRef.current;
    if (!el) return;
    const newHeight = el.scrollHeight;
    const raf = requestAnimationFrame(() => { el.style.height = `${newHeight}px`; });
    const settle = setTimeout(() => { el.style.height = 'auto'; }, 350);
    return () => { cancelAnimationFrame(raf); clearTimeout(settle); };
  }, [selectedFlatId]);

  const selectedFlat = sortedFlats.find(f => f.id === selectedFlatId) || sortedFlats[0];
  const ownerName = (id: string | null) => owners.find(p => p.id === id)?.name || null;
  const hasNamedFlats = prop.flats.some(f => f.flat_address !== 'Whole Property');
  // Building-level "Mixed" state needs the actual distinct names for its tooltip — a bare
  // "Mixed" label with no way to see who's actually assigned isn't useful, just confusing.
  const mixedOwnerNames = Array.from(new Set(prop.flats.map(f => f.owner_id)))
    .map(id => ownerName(id) || 'Unassigned');
  const mixedManagerNames = Array.from(new Set(prop.flats.map(f => f.managed_by_id)))
    .map(id => ownerName(id) || 'Unassigned');

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={{
        transition: 'box-shadow 0.3s ease, background-color 0.2s ease',
        bgcolor: expanded ? palette.tokens.surfaceCreamStrong : palette.tokens.surfaceCard,
        border: 'none',
        boxShadow: expanded ? `0 6px 24px ${alpha(palette.tokens.ink, 0.09)}` : 'none',
      }}>
        <CardContent sx={{ py: 1.25, px: 2.5, '&:last-child': { pb: 1.25 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: 0.75 }}>
            <Box
              onClick={onToggleExpand}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 auto', cursor: 'pointer' }}
            >
              <Box sx={{
                flexShrink: 0, width: 36, height: 36, borderRadius: '9px', bgcolor: palette.tokens.white,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 1px 3px ${alpha(palette.tokens.ink, 0.08)}`,
              }}>
                {hasNamedFlats
                  ? <ApartmentIcon sx={{ fontSize: 18, color: palette.tokens.primary }} />
                  : <HomeIcon sx={{ fontSize: 18, color: palette.tokens.primary }} />}
              </Box>
              {editingName ? (
                <TextField
                  autoFocus
                  size="small"
                  value={nameDraft}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setNameDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitNameEdit();
                    if (e.key === 'Escape') { setNameDraft(prop.normalized_address); setEditingName(false); }
                  }}
                  onBlur={commitNameEdit}
                  sx={{ minWidth: 0, flex: '0 1 260px' }}
                  inputProps={{ style: { fontSize: '1.1rem', fontWeight: 700 } }}
                />
              ) : (
                <>
                  <Typography
                    variant="displaySm"
                    sx={{
                      fontSize: { xs: '1.25rem', md: '1.5rem' },
                      fontWeight: 700,
                      lineHeight: 1,
                      color: palette.tokens.ink,
                      flex: '0 1 auto',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'lining-nums',
                    }}
                  >
                    {prop.normalized_address}
                  </Typography>
                  <Tooltip title="Rename building">
                    <IconButton
                      size="small"
                      onClick={e => { e.stopPropagation(); setNameDraft(prop.normalized_address); setEditingName(true); }}
                      sx={{ color: palette.tokens.mutedSoft, flexShrink: 0, '&:hover': { color: palette.tokens.primary } }}
                    >
                      <EditIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {hasNamedFlats && (
                <Chip
                  label={`${prop.flat_count} ${prop.flat_count === 1 ? 'Flat' : 'Flats'}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.72rem', '& .MuiChip-label': { px: 0.75 }, borderColor: palette.tokens.mutedSoft, color: palette.tokens.muted, flexShrink: 0, ml: 1 }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Box>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: palette.tokens.mutedSoft, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
                  Owner
                </Typography>
                <Tooltip title={prop.owner_mixed ? mixedOwnerNames.join(', ') : ''}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={prop.owner_mixed ? '' : (prop.owner_id || '')}
                      displayEmpty
                      onChange={e => onAssignBuildingOwner(prop.normalized_address, e.target.value || null)}
                      renderValue={(val) => {
                        if (prop.owner_mixed) {
                          return <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.ink, fontWeight: 600 }}>{mixedOwnerNames.length} owners</Typography>;
                        }
                        if (!val) {
                          return <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.mutedSoft }}>Unassigned</Typography>;
                        }
                        return <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.ink, fontWeight: 600 }}>{ownerName(val as string)}</Typography>;
                      }}
                    >
                      <MenuItem value=""><em>Unassigned</em></MenuItem>
                      {owners.filter(o => o.is_owner).map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Tooltip>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: palette.tokens.mutedSoft, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
                  Managed by
                </Typography>
                <Tooltip title={prop.managed_by_mixed ? mixedManagerNames.join(', ') : ''}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={prop.managed_by_mixed ? '' : (prop.managed_by_id || '')}
                      displayEmpty
                      onChange={e => onAssignBuildingManager(prop.normalized_address, e.target.value || null)}
                      renderValue={(val) => {
                        if (prop.managed_by_mixed) {
                          return <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.ink, fontWeight: 600 }}>{mixedManagerNames.length} managers</Typography>;
                        }
                        if (!val) {
                          return <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.mutedSoft }}>Unassigned</Typography>;
                        }
                        return <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.ink, fontWeight: 600 }}>{ownerName(val as string)}</Typography>;
                      }}
                    >
                      <MenuItem value=""><em>Unassigned</em></MenuItem>
                      {owners.filter(o => o.is_manager).map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Tooltip>
              </Box>
            </Box>
            <Tooltip title="Delete property">
              <IconButton
                size="small"
                onClick={e => { e.stopPropagation(); onDeleteBuilding(prop); }}
                sx={{ color: palette.tokens.mutedSoft, flexShrink: 0, '&:hover': { color: palette.tokens.error } }}
              >
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onToggleExpand} sx={{ color: palette.tokens.mutedSoft, flexShrink: 0 }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </CardContent>

        <AnimatePresence initial={false}>
          {expanded && selectedFlat && (
            <motion.div
              key="content"
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              variants={CONTENT_VARIANTS}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <CardContent sx={{ pt: 0, px: 2.5, pb: 2.5, borderTop: `2px solid ${palette.tokens.hairline}` }}>
                <Box sx={{ mb: 2, mt: 2 }} />

                <Box ref={tabSectionRef} sx={{ overflow: 'hidden', transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  {/* Tab row — one tab per flat, dot shows whether an owner is assigned */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0 }}>
                    {sortedFlats.map(flat => {
                      const isSelected = flat.id === selectedFlatId;
                      const dotColor = flat.owner_id ? palette.tokens.success : palette.tokens.mutedSoft;
                      return (
                        <Box
                          key={flat.id}
                          onClick={() => handleSelectFlat(flat.id)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75,
                            borderRadius: '10px 10px 0 0',
                            bgcolor: isSelected ? palette.tokens.white : 'transparent',
                            border: isSelected ? `1px solid ${palette.tokens.hairline}` : '1px solid transparent',
                            borderBottom: isSelected ? `1px solid ${palette.tokens.white}` : 'none',
                            mb: isSelected ? '-1px' : 0,
                            cursor: 'pointer',
                            position: 'relative',
                            zIndex: isSelected ? 1 : 0,
                            transition: 'background-color 0.15s ease',
                            '&:hover': !isSelected ? { bgcolor: alpha(palette.tokens.white, 0.5) } : {},
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? palette.tokens.ink : palette.tokens.muted, whiteSpace: 'nowrap' }}>
                            {flatShortLabel(flat)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Content panel — the selected flat's own details */}
                  <Box sx={{ bgcolor: palette.tokens.white, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '0 8px 8px 8px', p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <FormControl size="small" variant="standard" sx={{ mb: 0.5 }}>
                          <Select
                            value={selectedFlat.unit_status}
                            onChange={e => onUpdateStatus(selectedFlat.id, e.target.value as UnitStatus)}
                            disableUnderline
                            renderValue={(val) => (
                              <Chip
                                label={val as string}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  bgcolor: alpha(unitStatusColor(val as UnitStatus, palette.tokens), 0.14),
                                  color: unitStatusColor(val as UnitStatus, palette.tokens),
                                  '& .MuiChip-label': { px: 1 },
                                }}
                              />
                            )}
                            sx={{ '& .MuiSelect-select': { py: 0, pr: '24px !important' } }}
                          >
                            {UNIT_STATUSES.map(s => (
                              <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem', display: 'flex', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: unitStatusColor(s, palette.tokens), flexShrink: 0 }} />
                                {s}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: palette.tokens.ink }}>
                          {fmtGBP(selectedFlat.monthly_rent)}/mo{selectedFlat.country ? ` · ${selectedFlat.country}` : ''}
                        </Typography>
                      </Box>
                      <Tooltip title="Delete flat">
                        <IconButton size="small" onClick={() => onDeleteFlat(selectedFlat)} sx={{ color: palette.tokens.muted, '&:hover': { color: palette.tokens.error }, flexShrink: 0 }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mt: 1.5 }}>
                      <SummaryRow
                        label="Tenancy"
                        value={selectedFlat.active_tenant_names.length
                          ? selectedFlat.active_tenant_names.join(', ')
                          : 'No tenant · click to add'}
                        onClick={() => onOpenTenancy(selectedFlat)}
                      />
                      {/* Ownership & management merged into Property details (2026-08-13) — it
                          saved through the exact same PUT endpoint anyway, so a separate dialog
                          for it was redundant. This row's own preview stays bedrooms/district
                          only though — rent's already shown above, and owner/manager is visible
                          by opening the dialog. */}
                      <SummaryRow
                        label="Property details"
                        value={[
                          selectedFlat.bedrooms ? `${selectedFlat.bedrooms} bed` : null,
                          selectedFlat.district || null,
                        ].filter(Boolean).join(' · ') || 'No details yet'}
                        onClick={() => onOpenPropertyDetails(selectedFlat)}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ pt: 1.25 }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => onAddFlatToBuilding(prop.normalized_address)}
                    sx={{ color: palette.tokens.muted, fontSize: 12, fontWeight: 600, px: 0 }}
                  >
                    Add flat/office to this property
                  </Button>
                </Box>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </Box>
  );
};

const PropertiesPage: React.FC = () => {
  const { palette } = useTheme();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [properties, setProperties] = useState<PropertyGroup[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);

  // One marker per BUILDING (not per flat) for "Show on map" — every flat in a building shares
  // one physical location, so per-flat markers would just overlap identically. Built
  // client-side from the properties already held in state — no extra request/endpoint needed.
  const mapMarkers = useMemo<PropertyMapMarker[]>(() => {
    return properties
      .map(prop => {
        const geocodedFlat = prop.flats.find(f => f.address_verified);
        if (!geocodedFlat || geocodedFlat.latitude === null || geocodedFlat.longitude === null) return null;
        const ownerLabel = !prop.owner_mixed && prop.owner_id
          ? owners.find(o => o.id === prop.owner_id)?.name || null
          : null;
        return {
          normalized_address: prop.normalized_address,
          latitude: geocodedFlat.latitude,
          longitude: geocodedFlat.longitude,
          formatted_address: geocodedFlat.formatted_address,
          flat_count: prop.flat_count,
          owner_name: ownerLabel,
        };
      })
      .filter((m): m is PropertyMapMarker => m !== null);
  }, [properties, owners]);

  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [hasCertificateProperties, setHasCertificateProperties] = useState(false);
  const [buildingFromCertificates, setBuildingFromCertificates] = useState(false);

  // add/edit flat dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<FlatRecord | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  // A failed save (e.g. a 400 from the backend) previously failed silently — button just
  // re-enabled with no explanation. Surfaced directly in the dialog instead.
  const [saveError, setSaveError] = useState<string | null>(null);
  // 'Whole property' vs 'Building with flats/offices' — Add-only, UI-side choice that just
  // decides whether flat_address is auto-locked to the literal 'Whole Property' or built from
  // unitType + unitNumber below. Locked (toggle hidden) when adding a unit to a building that
  // already exists, since that context already implies a building with flats/offices.
  const [propertyKind, setPropertyKind] = useState<'whole' | 'building'>('whole');
  const [kindLocked, setKindLocked] = useState(false);
  // Only meaningful when propertyKind === 'building' — a building can mix flats and offices
  // (per direct request), so the unit's own type is chosen per-add, not fixed per-building.
  // flat_address is composed from these two on save as "Flat 3" / "Office 2", not typed as a
  // full address — see handleSave.
  const [unitType, setUnitType] = useState<'flat' | 'office'>('flat');
  const [unitNumber, setUnitNumber] = useState('');
  // The AddressFinder's own text box — separate from form.normalized_address since Edit's
  // "Location" field must never touch normalized_address/flat_address, only lat/lng.
  const [locationQuery, setLocationQuery] = useState('');
  // "Enter manually" reveal in Property location — a one-way toggle for the District/area
  // fallback field, shown by default if there's already a district value to edit.
  const [manualLocationOpen, setManualLocationOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FlatRecord | null>(null);
  // Building-level delete (removes every flat sharing this normalized_address) — separate
  // confirmation from the single-flat one above, since it's a much bigger blast radius.
  const [deleteBuildingTarget, setDeleteBuildingTarget] = useState<PropertyGroup | null>(null);
  const [deletingBuilding, setDeletingBuilding] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);

  // tenancy dialog (FR-2.2/2.3 — tenants + tenancy terms, and the unit's tenancy history)
  const [tenancyDialogOpen, setTenancyDialogOpen] = useState(false);
  const [tenancyTarget, setTenancyTarget] = useState<FlatRecord | null>(null);
  const [tenancyHistory, setTenancyHistory] = useState<Tenancy[]>([]);
  const [tenancyHistoryLoading, setTenancyHistoryLoading] = useState(false);
  const [tenancyForm, setTenancyForm] = useState({
    start_date: todayISO(), end_date: '', rent_amount: '', currency: 'GBP',
    payment_frequency: 'monthly' as Tenancy['payment_frequency'],
    payment_due_day: '', deposit_amount: '', deposit_status: '' as '' | Tenancy['deposit_status'],
    notice_period_days: '', notice_served_date: '', tenant_ids: [] as string[],
    // Per-tenant rent share, keyed by tenant id — only used/shown when tenancyTarget.is_hmo.
    // rent_amount above still holds the whole-unit total, but for an HMO it's derived (summed)
    // from this map rather than typed directly — see handleSaveTenancy/openTenancyDialog.
    tenant_rents: {} as Record<string, string>,
  });
  const [addingTenant, setAddingTenant] = useState(false);
  const [newTenantDraft, setNewTenantDraft] = useState({ name: '', email: '', phone: '', preferred_contact_channel: '', id_reference: '', preferred_language: '' });
  const [creatingTenant, setCreatingTenant] = useState(false);
  // Editing an already-existing (reusable) Tenant's own contact info — separate from
  // newTenantDraft, which is for a brand-new tenant not yet saved anywhere.
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [editTenantDraft, setEditTenantDraft] = useState({ name: '', email: '', phone: '', preferred_contact_channel: '', id_reference: '', preferred_language: '' });
  const [savingTenantEdit, setSavingTenantEdit] = useState(false);
  // Permanently deleting a reusable Tenant record from the "Select or add tenant(s)" dropdown —
  // separate from ending a tenancy (which only unlinks/optionally deletes tenants attached to
  // that specific tenancy). Backend 409s if the tenant is still linked to any tenancy, so
  // deleteTenantError surfaces that instead of silently failing.
  const [deleteTenantTarget, setDeleteTenantTarget] = useState<Tenant | null>(null);
  const [deletingTenant, setDeletingTenant] = useState(false);
  const [deleteTenantError, setDeleteTenantError] = useState<string | null>(null);
  const [savingTenancy, setSavingTenancy] = useState(false);
  const activeTenancy = tenancyHistory.find(t => t.status === 'Active') || null;
  const [clearHistoryConfirmOpen, setClearHistoryConfirmOpen] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  // payment_reference lives on PropertyRent (it's a flat-level bank-matching detail, not a
  // per-tenancy one — see rent_matcher.py), but per direct feedback it's edited from here, in
  // the Tenancy dialog's Rent info section, rather than Property details.
  const [paymentReferenceDraft, setPaymentReferenceDraft] = useState('');
  // Confirmation before ending a tenancy — carries whether this is a normal end vs. early
  // termination, and whether the linked tenant record(s) should be purged too (opt-in, unchecked
  // by default, since a tenant is reusable and the landlord may want to keep them on file).
  // Collapsed from two separate actions (End tenancy / Terminate early) to one — both did the
  // exact same thing operationally (end today, flip unit status to Vacant, optional tenant
  // purge), differing only in which of two status labels ('Ended' vs 'Terminated') got stored,
  // a distinction that wasn't worth two buttons and a "which one do I click" decision.
  const [endTenancyConfirm, setEndTenancyConfirm] = useState(false);
  const [deleteTenantsOnEnd, setDeleteTenantsOnEnd] = useState(false);
  // Surfaces a failed save/end-tenancy request — previously these silently did nothing on a
  // non-ok response, making a real backend rejection look identical to "editing doesn't work".
  const [tenancySaveError, setTenancySaveError] = useState<string | null>(null);
  // Manually switching a flat's status (via its status pill) to something that implies "no
  // tenant" (Vacant/Not lettable) while an Active tenancy still exists is rejected by the
  // backend (409) rather than silently orphaning that tenancy — this carries what's needed to
  // show a confirmation and, if accepted, actually end the tenancy before retrying the status change.
  const [statusChangeConflict, setStatusChangeConflict] = useState<{
    flatId: string; tenancyId: string; tenantNames: string[]; requestedStatus: UnitStatus;
  } | null>(null);
  const [resolvingStatusConflict, setResolvingStatusConflict] = useState(false);

  // owners management
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [editOwnerTarget, setEditOwnerTarget] = useState<Owner | null>(null);
  const [ownerForm, setOwnerForm] = useState({
    name: '', email: '', phone: '',
    entity_type: 'individual' as 'individual' | 'business',
    is_owner: true, is_manager: true,
    country: '', vat_number: '',
  });
  const [savingOwner, setSavingOwner] = useState(false);
  const [deleteOwnerTarget, setDeleteOwnerTarget] = useState<Owner | null>(null);
  // "See more" overflow — the compact sidebar panel caps at OWNER_PANEL_CAP owner cards so it
  // never grows taller than the welcome hero; anything past the cap lives here instead.
  const [ownersOverflowOpen, setOwnersOverflowOpen] = useState(false);
  // Which role's profiles the sidebar panel (and its overflow dialog) is showing — same
  // pseudo-tab pattern as AccountingPage.tsx's "Your banks"/"Your statements" toggle. A profile
  // with both roles (is_owner && is_manager) appears under both tabs, since it genuinely is both.
  const [ownersPanelSource, setOwnersPanelSource] = useState<'owners' | 'managers'>('owners');

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchProperties = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/accounting/properties`, { headers: headers() });
      if (res.ok) {
        const d = await res.json();
        setProperties(d.properties || []);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, headers]);

  const fetchOwners = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${config.apiUrl}/api/accounting/owners`, { headers: headers() });
    if (res.ok) {
      const d = await res.json();
      setOwners(d.owners || []);
    }
  }, [token, headers]);

  const fetchCertificateAvailability = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${config.apiUrl}/api/property-tree`, { headers: headers() });
    if (!res.ok) return;
    const d = await res.json();
    setHasCertificateProperties((d.tree_data?.length || 0) > 0);
  }, [token, headers]);

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const fetchTenants = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${config.apiUrl}/api/accounting/tenants`, { headers: headers() });
    if (res.ok) {
      const d = await res.json();
      setTenants(d.tenants || []);
    }
  }, [token, headers]);

  useEffect(() => {
    fetchProperties();
    fetchOwners();
    fetchCertificateAvailability();
    fetchTenants();
  }, [fetchProperties, fetchOwners, fetchCertificateAvailability, fetchTenants]);

  const toggleExpand = (address: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      next.has(address) ? next.delete(address) : next.add(address);
      return next;
    });
  };

  // ── build from certificates ─────────────────────────────────────────────────

  const handleBuildFromCertificates = async () => {
    setBuildingFromCertificates(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/accounting/build-from-certificates`, {
        method: 'POST', headers: headers(),
      });
      if (res.ok) {
        const d = await res.json();
        setProperties(d.properties || []);
      }
    } finally {
      setBuildingFromCertificates(false);
    }
  };

  // ── flat dialog helpers ──────────────────────────────────────────────────────

  const openAdd = useCallback((prefillAddress?: string) => {
    setEditTarget(null);
    setSaveError(null);
    // Adding a flat to an existing building — a building can't span countries, so inherit the
    // country the rest of its flats already share (still editable, just not re-typed every time).
    const existingBuilding = prefillAddress ? properties.find(p => p.normalized_address === prefillAddress) : null;
    const inheritedCountry = existingBuilding?.flats.find(f => f.country)?.country || '';
    // Same reasoning as country — a building is in one neighbourhood, so inherit it too.
    const inheritedDistrict = existingBuilding?.flats.find(f => f.district)?.district || '';
    // Same again for the building's own geocoding — every flat in it sits at the same physical
    // location, so a second flat added to an already-verified building should stay verified
    // rather than reverting to "unverified" for no reason a user would expect.
    const geocodedFlat = existingBuilding?.flats.find(f => f.address_verified);
    // Coming from "Add flat/office to this property" on a building that already exists — that
    // context already means "building with flats/offices", so lock the choice and skip the toggle.
    const kind: 'whole' | 'building' = prefillAddress ? 'building' : 'whole';
    setPropertyKind(kind);
    setKindLocked(!!prefillAddress);
    setUnitType('flat');
    setUnitNumber('');
    setLocationQuery(geocodedFlat?.formatted_address || '');
    setManualLocationOpen(!!inheritedDistrict);
    setForm({
      ...EMPTY_FORM,
      normalized_address: prefillAddress || '',
      country: inheritedCountry,
      district: inheritedDistrict,
      latitude: geocodedFlat?.latitude ?? null,
      longitude: geocodedFlat?.longitude ?? null,
      formatted_address: geocodedFlat?.formatted_address ?? null,
      place_id: null,
      rent_effective_from: todayISO(),
    });
    setDialogOpen(true);
  }, [properties]);

  // Switching kind clears any number already typed — a stale "3" left over from Building mode
  // has no meaning once switched to Whole property, and vice versa.
  const selectPropertyKind = (kind: 'whole' | 'building') => {
    setPropertyKind(kind);
    setUnitNumber('');
  };

  // AddressFinder's onSelect: a real pick sets lat/lng/formatted_address; null (free typing,
  // or clearing) wipes them — "verified" only ever means "just picked from the list".
  const selectAddressMatch = (match: AddressMatch | null) => {
    setForm(f => ({
      ...f,
      latitude: match?.latitude ?? null,
      longitude: match?.longitude ?? null,
      formatted_address: match?.label ?? null,
      place_id: match?.place_id ?? null,
    }));
  };

  // Deep-link from Accounting's "Add flat" shortcut (?add=1) — opens the dialog immediately
  // instead of making the user find the button again on a page they just navigated to.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('add') === '1') {
      const address = params.get('address');
      openAdd(address || undefined);
      navigate('/properties', { replace: true });
    }
    if (params.get('map') === '1') {
      setMapDialogOpen(true);
      navigate('/properties', { replace: true });
    }
  }, [location.search, openAdd, navigate]);

  const openEdit = (flat: FlatRecord) => {
    setEditTarget(flat);
    setSaveError(null);
    // Reset so a stale value from a previous Add session can't leak into Edit's own rendering
    // (the toggle/type-dropdown are hidden in Edit regardless, but keep state consistent).
    setPropertyKind('whole');
    setKindLocked(false);
    setUnitType('flat');
    setUnitNumber('');
    // Prefill the "Postcode / zip code" search field. Flats auto-created by "Build from
    // certificates" (handleBuildFromCertificates) aren't geocoded yet (no formatted_address),
    // but their flat_address is built there as "street, flat, ZIPCODE" — the last comma segment
    // is a genuine postcode whenever the source certificate had one. Previously this fell back to
    // flat.normalized_address, which is a street/building name, never a postcode, and was
    // actively misleading in that search box.
    const flatAddressParts = flat.flat_address.split(',').map(p => p.trim());
    const likelyZipcode = flatAddressParts.length >= 3 ? flatAddressParts[flatAddressParts.length - 1] : '';
    setLocationQuery(flat.formatted_address || likelyZipcode);
    setManualLocationOpen(!!flat.district);
    setForm({
      normalized_address: flat.normalized_address,
      flat_address: flat.flat_address,
      monthly_rent: flat.monthly_rent,
      country: flat.country || '',
      district: flat.district || '',
      bedrooms: flat.bedrooms?.toString() || '',
      bathrooms: flat.bathrooms?.toString() || '',
      unit_status: flat.unit_status || 'Vacant',
      is_hmo: flat.is_hmo || false,
      rent_due_day: flat.rent_due_day?.toString() || '',
      tenant_name: flat.tenant_name || '',
      tenant_email: flat.tenant_email || '',
      tenant_phone: flat.tenant_phone || '',
      deposit_amount: flat.deposit_amount || '',
      deposit_lodged: flat.deposit_lodged,
      rent_effective_from: todayISO(),
      latitude: flat.latitude,
      longitude: flat.longitude,
      formatted_address: flat.formatted_address,
      place_id: null,
      owner_id: flat.owner_id || '',
      managed_by_id: flat.managed_by_id || '',
      management_fee_type: flat.management_fee_type || 'percent',
      management_fee_value: flat.management_fee_value || '',
    });
    setDialogOpen(true);
  };

  // Edit keeps flat_address as freeform text (unchanged, long-standing behavior). Add composes
  // it from the kind/type/number choices above instead of asking for a full address — "Flat 3"
  // / "Office 2" / "Whole Property" — since the building address already carries the street.
  const resolvedFlatAddress = editTarget
    ? form.flat_address.trim()
    : propertyKind === 'whole'
      ? 'Whole Property'
      : `${unitType === 'office' ? 'Office' : 'Flat'} ${unitNumber.trim()}`.trim();

  const handleSave = async () => {
    if (!form.normalized_address.trim() || !resolvedFlatAddress || (!editTarget && propertyKind === 'building' && !unitNumber.trim())) return;
    setSaving(true);
    setSaveError(null);
    try {
      const isThirdPartyManaged = !!form.managed_by_id && form.managed_by_id !== form.owner_id;
      const body = {
        normalized_address: form.normalized_address.trim(),
        flat_address: resolvedFlatAddress,
        country: form.country || null,
        district: form.district || null,
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
        bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
        // Always sent (Add and Edit) — safe to resend unchanged values, and this is exactly how
        // Edit's "Location" field (AddressFinder, re-verifying or clearing an address) commits.
        latitude: form.latitude,
        longitude: form.longitude,
        formatted_address: form.formatted_address,
        place_id: form.place_id,
        // Rent/rent-due-day are sent on both Add and Edit — this dialog is exactly where a
        // landlord sets or updates what a flat is billed, independent of who (if anyone)
        // currently occupies it (you shouldn't have to start a tenancy just to record a rent
        // figure on a still-vacant flat). Rent is no longer required up front either (see
        // propertyKind above) — default to '0.00' rather than sending '', which the backend's
        // float('') parse rejects with a 400.
        monthly_rent: form.monthly_rent || '0.00',
        rent_effective_from: form.rent_effective_from,
        rent_due_day: form.rent_due_day ? parseInt(form.rent_due_day) : null,
        // Sent on both Add and Edit too — is_hmo lives on the flat itself, not any one tenancy
        // (see PropertyRent.is_hmo), so it belongs alongside rent, not gated to Add-only.
        is_hmo: form.is_hmo,
        // unit_status stays Add-only here — an existing flat's status has its own dedicated
        // quick-edit pill on the flat panel, which already handles the active-tenancy-conflict
        // case (confirm-and-end-tenancy) properly; duplicating status editing into this dialog
        // too would just be a second, less-well-handled path to the same 409.
        ...(!editTarget && { unit_status: form.unit_status }),
        tenant_name: form.tenant_name || null,
        tenant_email: form.tenant_email || null,
        tenant_phone: form.tenant_phone || null,
        deposit_amount: form.deposit_amount || null,
        deposit_lodged: form.deposit_lodged,
        owner_id: form.owner_id || null,
        managed_by_id: form.managed_by_id || null,
        management_fee_type: isThirdPartyManaged ? form.management_fee_type : null,
        management_fee_value: isThirdPartyManaged ? form.management_fee_value : null,
      };
      const url = editTarget
        ? `${config.apiUrl}/api/accounting/properties/${editTarget.id}`
        : `${config.apiUrl}/api/accounting/properties`;
      const res = await fetch(url, {
        method: editTarget ? 'PUT' : 'POST',
        headers: headers(),
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setDialogOpen(false);
        fetchProperties(true);
      } else {
        const err = await res.json().catch(() => null);
        setSaveError(err?.error || 'Something went wrong saving this flat — please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await fetch(`${config.apiUrl}/api/accounting/properties/${deleteTarget.id}`, {
      method: 'DELETE', headers: headers(),
    });
    setDeleteTarget(null);
    fetchProperties(true);
  };

  const handleDeleteBuilding = async () => {
    if (!deleteBuildingTarget) return;
    setDeletingBuilding(true);
    try {
      await fetch(`${config.apiUrl}/api/accounting/properties/delete-building`, {
        method: 'DELETE',
        headers: headers(),
        body: JSON.stringify({ normalized_address: deleteBuildingTarget.normalized_address }),
      });
      setDeleteBuildingTarget(null);
      fetchProperties(true);
    } finally {
      setDeletingBuilding(false);
    }
  };

  // ── owner / manager assignment ────────────────────────────────────────────

  const handleAssignBuildingOwner = async (normalizedAddress: string, ownerId: string | null) => {
    await fetch(`${config.apiUrl}/api/accounting/properties/assign-owner`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ normalized_address: normalizedAddress, owner_id: ownerId }),
    });
    fetchProperties(true);
  };

  const handleAssignBuildingManager = async (normalizedAddress: string, managedById: string | null) => {
    await fetch(`${config.apiUrl}/api/accounting/properties/assign-owner`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ normalized_address: normalizedAddress, managed_by_id: managedById }),
    });
    fetchProperties(true);
  };

  const handleRenameBuilding = async (oldAddress: string, newAddress: string) => {
    await fetch(`${config.apiUrl}/api/accounting/properties/rename-building`, {
      method: 'PUT', headers: headers(),
      body: JSON.stringify({ normalized_address: oldAddress, new_normalized_address: newAddress }),
    });
    fetchProperties(true);
  };

  // Shared by the status pill and (if a future entry point needs it) anything else that writes
  // unit_status directly — a 409 here means the backend rejected it because an Active tenancy
  // would be orphaned; the caller shows a confirmation instead of silently doing nothing.
  const applyUnitStatus = async (flatId: string, unitStatus: UnitStatus): Promise<boolean> => {
    const res = await fetch(`${config.apiUrl}/api/accounting/properties/${flatId}`, {
      method: 'PUT', headers: headers(),
      body: JSON.stringify({ unit_status: unitStatus }),
    });
    if (res.status === 409) {
      const d = await res.json().catch(() => null);
      if (d?.error === 'active_tenancy_conflict') {
        setStatusChangeConflict({
          flatId, tenancyId: d.tenancy_id, tenantNames: d.tenant_names || [], requestedStatus: unitStatus,
        });
        return false;
      }
    }
    return res.ok;
  };

  const handleUpdateFlatStatus = async (flatId: string, unitStatus: UnitStatus) => {
    await applyUnitStatus(flatId, unitStatus);
    fetchProperties(true);
  };

  // User confirmed "yes, end the tenancy" from the statusChangeConflict dialog — end it (as an
  // early/out-of-band termination, since this is a manual override mid-tenancy, not the tenancy
  // naturally reaching its end date), then apply whichever exact status was originally requested
  // if it's not the generic 'Vacant' that ending a tenancy already resolves to on its own.
  const handleConfirmStatusConflict = async () => {
    if (!statusChangeConflict) return;
    setResolvingStatusConflict(true);
    try {
      await fetch(`${config.apiUrl}/api/accounting/tenancies/${statusChangeConflict.tenancyId}/end`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ terminated: true, delete_tenants: false }),
      });
      if (statusChangeConflict.requestedStatus !== 'Vacant') {
        await applyUnitStatus(statusChangeConflict.flatId, statusChangeConflict.requestedStatus);
      }
      setStatusChangeConflict(null);
      fetchProperties(true);
      fetchTenants();
    } finally {
      setResolvingStatusConflict(false);
    }
  };

  // ── tenancy dialog (FR-2.2/2.3 — current tenancy + full history) ───────────

  const resetTenancyForm = () => {
    setTenancyForm({
      start_date: todayISO(), end_date: '', rent_amount: '', currency: 'GBP', payment_frequency: 'monthly',
      payment_due_day: '', deposit_amount: '', deposit_status: '',
      notice_period_days: '', notice_served_date: '', tenant_ids: [], tenant_rents: {},
    });
    setAddingTenant(false);
    setNewTenantDraft({ name: '', email: '', phone: '', preferred_contact_channel: '', id_reference: '', preferred_language: '' });
    setEditingTenantId(null);
  };

  const openTenancyDialog = async (flat: FlatRecord) => {
    setTenancyTarget(flat);
    resetTenancyForm();
    setPaymentReferenceDraft(flat.payment_reference || '');
    setTenancyDialogOpen(true);
    setTenancyHistoryLoading(true);
    setTenancySaveError(null);
    // Clear before fetching — otherwise a failed fetch below would leave activeTenancy pointing
    // at whichever flat's tenancy was open previously, while tenancyTarget already points at
    // this new flat, and a save could PUT to the wrong tenancy id.
    setTenancyHistory([]);
    try {
      const res = await fetch(`${config.apiUrl}/api/accounting/properties/${flat.id}/tenancies`, { headers: headers() });
      if (res.ok) {
        const d = await res.json();
        const history: Tenancy[] = d.tenancies || [];
        setTenancyHistory(history);
        const active = history.find(t => t.status === 'Active');
        if (active) {
          setTenancyForm({
            start_date: active.start_date, end_date: active.end_date || '',
            rent_amount: active.rent_amount, currency: active.currency || 'GBP', payment_frequency: active.payment_frequency,
            payment_due_day: active.payment_due_day?.toString() || '',
            deposit_amount: active.deposit_amount || '', deposit_status: active.deposit_status || '',
            notice_period_days: active.notice_period_days?.toString() || '',
            notice_served_date: active.notice_served_date || '',
            tenant_ids: active.tenants.map(t => t.id),
            tenant_rents: Object.fromEntries(
              active.tenants.filter(t => t.rent_amount != null).map(t => [t.id, t.rent_amount as string])
            ),
          });
        } else {
          setTenancyForm(f => ({ ...f, rent_amount: flat.monthly_rent }));
        }
      }
    } finally {
      setTenancyHistoryLoading(false);
    }
  };

  // Creates the tenant for real right away (not a locally-held draft) and immediately selects
  // them on this tenancy — per direct feedback, deferring creation to the final Save meant a
  // just-added tenant didn't show up as a selected option until after the whole dialog was saved.
  const handleCreateAndAddTenant = async () => {
    if (!newTenantDraft.name.trim()) return;
    setCreatingTenant(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/accounting/tenants`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify(newTenantDraft),
      });
      if (res.ok) {
        const d = await res.json();
        const tenant: Tenant = d.tenant;
        setTenants(list => [...list, tenant]);
        setTenancyForm(f => ({ ...f, tenant_ids: [...f.tenant_ids, tenant.id] }));
        setNewTenantDraft({ name: '', email: '', phone: '', preferred_contact_channel: '', id_reference: '', preferred_language: '' });
        setAddingTenant(false);
      }
    } finally {
      setCreatingTenant(false);
    }
  };

  const openEditTenant = (tenant: Tenant) => {
    setAddingTenant(false);
    setEditingTenantId(tenant.id);
    setEditTenantDraft({
      name: tenant.name, email: tenant.email || '', phone: tenant.phone || '',
      preferred_contact_channel: tenant.preferred_contact_channel || '',
      id_reference: tenant.id_reference || '', preferred_language: tenant.preferred_language || '',
    });
  };

  const handleSaveTenantEdit = async () => {
    if (!editingTenantId || !editTenantDraft.name.trim()) return;
    setSavingTenantEdit(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/accounting/tenants/${editingTenantId}`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify(editTenantDraft),
      });
      if (res.ok) {
        setEditingTenantId(null);
        fetchTenants();
      }
    } finally {
      setSavingTenantEdit(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!deleteTenantTarget) return;
    setDeletingTenant(true);
    setDeleteTenantError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/accounting/tenants/${deleteTenantTarget.id}`, {
        method: 'DELETE', headers: headers(),
      });
      if (res.ok) {
        setTenants(list => list.filter(t => t.id !== deleteTenantTarget.id));
        setTenancyForm(f => ({ ...f, tenant_ids: f.tenant_ids.filter(id => id !== deleteTenantTarget.id) }));
        if (editingTenantId === deleteTenantTarget.id) setEditingTenantId(null);
        setDeleteTenantTarget(null);
      } else {
        const d = await res.json().catch(() => ({}));
        setDeleteTenantError(d.error || 'This tenant is still linked to a tenancy and can\'t be deleted.');
      }
    } finally {
      setDeletingTenant(false);
    }
  };

  const handleSaveTenancy = async () => {
    if (!tenancyTarget) return;
    const isHmo = tenancyTarget.is_hmo;
    // An HMO's rent is derived (summed) from each tenant's own share instead of typed directly —
    // see the "Rent per tenant" block above and _sum_tenant_rents server-side.
    const hmoTotal = Object.values(tenancyForm.tenant_rents).reduce((s, v) => s + (Number(v) || 0), 0);
    if (isHmo ? hmoTotal <= 0 : !tenancyForm.rent_amount) return;
    if (!tenancyForm.start_date) return;
    if (!tenancyForm.tenant_ids.length) return;
    setSavingTenancy(true);
    setTenancySaveError(null);
    try {
      const body = {
        start_date: tenancyForm.start_date,
        end_date: tenancyForm.end_date || null,
        ...(isHmo ? { tenant_rents: tenancyForm.tenant_rents } : { rent_amount: tenancyForm.rent_amount }),
        currency: tenancyForm.currency,
        payment_frequency: tenancyForm.payment_frequency,
        payment_due_day: tenancyForm.payment_due_day ? parseInt(tenancyForm.payment_due_day) : null,
        deposit_amount: tenancyForm.deposit_amount || null,
        deposit_status: tenancyForm.deposit_amount ? (tenancyForm.deposit_status || 'held') : null,
        notice_period_days: tenancyForm.notice_period_days ? parseInt(tenancyForm.notice_period_days) : null,
        notice_served_date: tenancyForm.notice_served_date || null,
        tenant_ids: tenancyForm.tenant_ids,
        status: 'Active' as const,
      };
      const url = activeTenancy
        ? `${config.apiUrl}/api/accounting/tenancies/${activeTenancy.id}`
        : `${config.apiUrl}/api/accounting/properties/${tenancyTarget.id}/tenancies`;
      const res = await fetch(url, {
        method: activeTenancy ? 'PUT' : 'POST',
        headers: headers(),
        body: JSON.stringify(body),
      });
      if (res.ok) {
        if (paymentReferenceDraft !== (tenancyTarget.payment_reference || '')) {
          await fetch(`${config.apiUrl}/api/accounting/properties/${tenancyTarget.id}`, {
            method: 'PUT', headers: headers(),
            body: JSON.stringify({ payment_reference: paymentReferenceDraft || null }),
          });
        }
        setTenancyDialogOpen(false);
        fetchProperties(true);
        fetchTenants();
      } else {
        const err = await res.json().catch(() => null);
        setTenancySaveError(err?.error || 'Something went wrong saving this tenancy — please try again.');
      }
    } finally {
      setSavingTenancy(false);
    }
  };

  const handleEndTenancy = async (deleteTenants: boolean) => {
    if (!activeTenancy) return;
    setSavingTenancy(true);
    setTenancySaveError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/accounting/tenancies/${activeTenancy.id}/end`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ terminated: false, delete_tenants: deleteTenants }),
      });
      if (res.ok) {
        setEndTenancyConfirm(false);
        setTenancyDialogOpen(false);
        fetchProperties(true);
        fetchTenants();
      } else {
        const err = await res.json().catch(() => null);
        setEndTenancyConfirm(false);
        setTenancySaveError(err?.error || 'Something went wrong ending this tenancy — please try again.');
      }
    } finally {
      setSavingTenancy(false);
    }
  };

  const handleClearHistory = async () => {
    if (!tenancyTarget) return;
    setClearingHistory(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/accounting/properties/${tenancyTarget.id}/tenancies/history`, {
        method: 'DELETE', headers: headers(),
      });
      if (res.ok) {
        setTenancyHistory(h => h.filter(t => t.status === 'Active'));
        setClearHistoryConfirmOpen(false);
      }
    } finally {
      setClearingHistory(false);
    }
  };

  // ── owner CRUD ─────────────────────────────────────────────────────────────

  const openAddOwner = () => {
    setEditOwnerTarget(null);
    setOwnerForm({
      name: '', email: '', phone: '', entity_type: 'individual',
      is_owner: true, is_manager: true, country: '', vat_number: '',
    });
    setOwnerDialogOpen(true);
  };

  const openEditOwner = (owner: Owner) => {
    setEditOwnerTarget(owner);
    setOwnerForm({
      name: owner.name, email: owner.email || '', phone: owner.phone || '',
      entity_type: owner.entity_type || 'individual',
      is_owner: owner.is_owner, is_manager: owner.is_manager,
      country: owner.country || '', vat_number: owner.vat_number || '',
    });
    setOwnerDialogOpen(true);
  };

  const handleSaveOwner = async () => {
    if (!ownerForm.name.trim() || (!ownerForm.is_owner && !ownerForm.is_manager)) return;
    setSavingOwner(true);
    try {
      const body = {
        name: ownerForm.name.trim(),
        email: ownerForm.email.trim() || null,
        phone: ownerForm.phone.trim() || null,
        entity_type: ownerForm.entity_type,
        is_owner: ownerForm.is_owner,
        is_manager: ownerForm.is_manager,
        country: ownerForm.country || null,
        vat_number: ownerForm.entity_type === 'business' ? (ownerForm.vat_number.trim() || null) : null,
      };
      const url = editOwnerTarget
        ? `${config.apiUrl}/api/accounting/owners/${editOwnerTarget.id}`
        : `${config.apiUrl}/api/accounting/owners`;
      const res = await fetch(url, {
        method: editOwnerTarget ? 'PUT' : 'POST',
        headers: headers(),
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setOwnerDialogOpen(false);
        fetchOwners();
      }
    } finally {
      setSavingOwner(false);
    }
  };

  const handleDeleteOwner = async () => {
    if (!deleteOwnerTarget) return;
    await fetch(`${config.apiUrl}/api/accounting/owners/${deleteOwnerTarget.id}`, {
      method: 'DELETE', headers: headers(),
    });
    setDeleteOwnerTarget(null);
    fetchOwners();
    fetchProperties(true);
  };

  const ownerName = (id: string | null) => owners.find(p => p.id === id)?.name || null;
  const selectedFormOwner = owners.find(p => p.id === form.owner_id) || null;

  // ── welcome hero — same shape as DashboardPage.tsx's "Portfolio" hero (coral banner, date +
  // status pill, big serif greeting, cream stats footer), sized ~10% down from that reference
  // (an initial 20% cut read as too small on a second look) — same ratio kept on both pages so
  // they stay visually consistent. Repurposed 2026-08-13 from an "admin attention" summary
  // (unowned/unmanaged flat counts) to an occupancy/rent summary, since the user is putting
  // real rent figures on flats now — that's the more useful headline signal than ownership admin.
  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const firstName = (user?.name?.split(' ')[0] || '').replace(/^./, c => c.toUpperCase());
  const totalFlats = properties.reduce((sum, p) => sum + p.flat_count, 0);
  const occupiedFlats = properties.reduce((sum, p) => sum + p.flats.filter(f => f.unit_status === 'Occupied').length, 0);
  const occupancyRatePct = totalFlats > 0 ? Math.round((occupiedFlats / totalFlats) * 100) : 0;
  // "Collected" = rent actually coming in right now (occupied flats only). "Potential" = what
  // every rented flat would bring in if it were occupied too — the denominator for the bracketed
  // %, so an unoccupied flat that's never had a rent figure set doesn't drag the ratio down for
  // no reason (it's not lettable-ready yet, not "lost income").
  const collectedRent = properties.reduce((sum, p) => sum + p.flats
    .filter(f => f.unit_status === 'Occupied')
    .reduce((s, f) => s + Number(f.monthly_rent || 0), 0), 0);
  const potentialRent = properties.reduce((sum, p) => sum + p.flats
    .filter(f => Number(f.monthly_rent || 0) > 0)
    .reduce((s, f) => s + Number(f.monthly_rent || 0), 0), 0);
  const rentPotentialPct = potentialRent > 0 ? Math.round((collectedRent / potentialRent) * 100) : null;

  const heroBg = palette.tokens.primaryActive;
  const heroOnColor = palette.tokens.onPrimary;
  const heroSubtleColor = alpha(palette.tokens.onPrimary, 0.78);
  const heroAccentColor = palette.tokens.accentAmber;

  // ── render ───────────────────────────────────────────────────────────────────

  return (
    // minHeight guarantees this page always overflows its scroll container by a little, even
    // with zero properties/nothing expanded — so the scrollbar is present from the very first
    // render (same as CertificatesPage, which just happens to always have enough content to
    // overflow). Without this, the scrollbar only appears once expanding a building pushes the
    // page past one viewport height, and its appearance is what was narrowing the page and
    // reflowing the hero — height:'100%' above was letting the page be exactly viewport-height
    // (no overflow) whenever nothing was expanded.
    <Box sx={{ flex: 1, p: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '100%', minHeight: 'calc(100% + 1px)', boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Same flex:3/flex:1 top-row split as the Certificates/Dashboard hero + its upload card —
          Owners takes the sidebar slot here instead. maxWidth trims a small buffer (roughly a
          scrollbar's width) off the row itself, on top of the scrollbar-gutter reservation on the
          page's scroll container (App.tsx) — belt-and-braces so these two cards never visibly
          shrink/reflow from a scrollbar appearing or disappearing as buildings are expanded. */}
      <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2, mb: 3, alignItems: 'stretch', maxWidth: { md: 'calc(100% - 20px)' } }}>
      <Box sx={{
        flex: 3,
        borderRadius: '14px', overflow: 'hidden', mb: { xs: 2, md: 0 },
        border: `1px solid ${palette.tokens.hairline}`, boxShadow: `0 1px 3px ${alpha(palette.tokens.ink, 0.06)}`,
      }}>
        <Box sx={{ bgcolor: heroBg, p: { xs: 2.7, md: 3.6 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.8, flexWrap: 'wrap' }}>
            <Box sx={{ bgcolor: alpha(heroOnColor, 0.18), borderRadius: 10, px: 1.35, py: 0.36 }}>
              <Typography sx={{ fontSize: '0.65rem', color: heroOnColor, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              </Typography>
            </Box>
            {!loading && properties.length === 0 && (
              <Box sx={{ bgcolor: alpha(heroOnColor, 0.18), border: `1px solid ${alpha(heroOnColor, 0.3)}`, borderRadius: 10, px: 1.35, py: 0.36 }}>
                <Typography sx={{ fontSize: '0.65rem', color: heroOnColor, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  Let's get started
                </Typography>
              </Box>
            )}
          </Box>

          <Typography
            variant="displaySm" component="h2"
            sx={{
              fontSize: { xs: '1.8rem', md: '2.52rem' }, fontWeight: 400, color: heroOnColor, lineHeight: 1.2, mb: 1.35,
              fontVariantNumeric: 'lining-nums',
              // Reserves room for two lines regardless of whether this sentence actually wraps to
              // one or two — its length varies with the flat/rent figures (and with whether a
              // vertical scrollbar is present, which nudges the available width), and without this
              // the hero (and the Profiles panel next to it, which matches its height via flex
              // stretch) would visibly grow/shrink shape whenever that wrapping changed, e.g. when
              // expanding a building added enough content below to introduce a scrollbar.
              minHeight: { xs: '4.32rem', md: '6.05rem' },
            }}
          >
            Good {timeOfDay}{firstName ? `, ${firstName}` : ''}.{' '}
            {!loading && (
              properties.length === 0 ? (
                <>Let's build your <Box component="span" sx={{ color: heroAccentColor }}>portfolio.</Box></>
              ) : owners.length === 0 ? (
                <>Let's add your first <Box component="span" sx={{ color: heroAccentColor }}>profile.</Box></>
              ) : (
                <>
                  <Box component="span" sx={{ color: heroAccentColor }}>
                    {occupiedFlats} of {totalFlats}
                  </Box>{' '}
                  {totalFlats === 1 ? 'flat' : 'flats'} occupied — {fmtGBP(collectedRent)}/mo collected.
                </>
              )
            )}
          </Typography>

          {!loading && (properties.length === 0 || owners.length === 0) && (
            <Typography sx={{ color: heroSubtleColor, fontSize: '0.81rem', lineHeight: 1.6 }}>
              {properties.length === 0
                ? 'Add your first flat and assign an owner to start tracking who it belongs to.'
                : 'Add a profile to start assigning ownership and management of your flats.'}
            </Typography>
          )}
        </Box>

        <Box sx={{ bgcolor: palette.tokens.canvas, p: { xs: 2.7, md: 3.6 } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: { xs: 2.25, sm: 2.7 } }}>
            {[
              { value: totalFlats, label: 'Properties' },
              {
                value: `${occupancyRatePct}%`,
                label: 'Occupancy rate',
                info: `Tracks how many of your flats are occupied today — ${occupiedFlats} of ${totalFlats} ${totalFlats === 1 ? 'flat is' : 'flats are'} currently occupied.`,
              },
              {
                value: fmtGBP(collectedRent),
                label: 'Rent collected',
                sub: rentPotentialPct !== null ? `${rentPotentialPct}% of potential` : undefined,
                info: `What you're actually receiving each month${rentPotentialPct !== null ? ` — the ${rentPotentialPct}% in brackets is against what you'd collect if every rented flat were occupied too` : ''}.`,
              },
            ].map(stat => (
              <Box key={stat.label}>
                <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 0.45 }} />
                <Typography variant="displaySm" component="div" sx={{ fontSize: { xs: '1.62rem', md: '1.98rem' }, color: palette.tokens.ink, fontWeight: 700, lineHeight: 1, textAlign: 'center', fontVariantNumeric: 'lining-nums' }}>
                  {loading ? '—' : stat.value}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, mt: 0.45 }}>
                  <Typography sx={{ fontSize: '0.74rem', color: palette.tokens.muted, textAlign: 'center' }}>{stat.label}</Typography>
                  {stat.info && (
                    <Tooltip title={stat.info}>
                      <InfoIcon sx={{ fontSize: 13, color: palette.tokens.mutedSoft, cursor: 'help' }} />
                    </Tooltip>
                  )}
                </Box>
                {!loading && stat.sub && (
                  <Typography sx={{ fontSize: '0.68rem', color: palette.tokens.mutedSoft, textAlign: 'center' }}>({stat.sub})</Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Profiles — right sidebar slot, same visual language as AccountingPage.tsx's "Your
          banks"/"Your statements" panel (pseudo-tab row with a "cut corner" into a white content
          panel below, count + action icons row, capped list of rows). Each profile can be
          assigned to a property as its owner, its manager, or both. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{
          height: '100%', borderRadius: '14px', border: `1px solid ${palette.tokens.hairline}`,
          bgcolor: palette.tokens.surfaceCreamStrong, p: 2, display: 'flex', flexDirection: 'column',
        }}>
          {/* Tab row — same pseudo-tab pattern as AccountingPage.tsx's "Your banks"/"Your
              statements" toggle: click a tab to switch which role's profiles this one card shows.
              A profile with both roles shows up under both tabs (see ownersPanelSource above). */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {(['owners', 'managers'] as const).map(s => {
              const isSelected = ownersPanelSource === s;
              return (
                <Box
                  key={s}
                  onClick={() => setOwnersPanelSource(s)}
                  sx={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, px: 1.5, py: 0.75,
                    borderRadius: '10px 10px 0 0',
                    bgcolor: isSelected ? palette.tokens.white : 'transparent',
                    border: isSelected ? `1px solid ${palette.tokens.hairline}` : '1px solid transparent',
                    borderBottom: isSelected ? `1px solid ${palette.tokens.white}` : 'none',
                    mb: isSelected ? '-1px' : 0,
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: isSelected ? 1 : 0,
                    transition: 'background-color 0.15s ease',
                    '&:hover': !isSelected ? { bgcolor: alpha(palette.tokens.white, 0.5) } : {},
                  }}
                >
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? palette.tokens.ink : palette.tokens.muted, whiteSpace: 'nowrap' }}>
                    {s === 'owners' ? 'Owners' : 'Managers'}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Content panel for the selected tab — same "cut corner" pairing with the tab row
              above as AccountingPage.tsx's bank panel. `flex: 1` so it fills whatever vertical
              space remains in this fixed-height card, same as the bank panel's content Box. */}
          <Box sx={{ bgcolor: palette.tokens.white, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '0 8px 8px 8px', p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {(() => {
              const filteredOwners = owners.filter(p => ownersPanelSource === 'owners' ? p.is_owner : p.is_manager);
              const overflowCount = filteredOwners.length - OWNER_PANEL_CAP;
              return (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: palette.tokens.ink, flex: 1 }}>
                      {filteredOwners.length > 0 ? `${filteredOwners.length} ${ownersPanelSource === 'owners' ? 'owner' : 'manager'}${filteredOwners.length === 1 ? '' : 's'}` : ''}
                    </Typography>
                    <Tooltip title="New profile">
                      <IconButton size="small" onClick={openAddOwner} sx={{ color: palette.tokens.muted }}>
                        <AddIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Individuals or businesses you can assign as an owner or manager of a building or flat.">
                      <InfoIcon sx={{ fontSize: 16, color: palette.tokens.muted, cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: OWNER_CARD_SIZES[2].gap }}>
                    {filteredOwners.slice(0, OWNER_PANEL_CAP).map(p => (
                      <OwnerCard
                        key={p.id}
                        owner={p}
                        size={2}
                        onEdit={() => openEditOwner(p)}
                        onDelete={() => setDeleteOwnerTarget(p)}
                      />
                    ))}

                    {overflowCount > 0 ? (
                      <Card
                        elevation={0}
                        onClick={() => setOwnersOverflowOpen(true)}
                        sx={{
                          border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, bgcolor: 'transparent',
                          cursor: 'pointer', transition: 'background-color 0.15s ease',
                          '&:hover': { bgcolor: palette.tokens.surfaceSoft },
                        }}
                      >
                        <CardContent sx={{ p: OWNER_CARD_SIZES[2].pad, '&:last-child': { pb: OWNER_CARD_SIZES[2].pad }, textAlign: 'center' }}>
                          <Typography sx={{ fontWeight: 600, fontSize: OWNER_CARD_SIZES[2].font, color: palette.tokens.primary }}>
                            See {overflowCount} more
                          </Typography>
                        </CardContent>
                      </Card>
                    ) : filteredOwners.length === 0 ? (
                      <Card
                        elevation={0}
                        onClick={openAddOwner}
                        sx={{
                          border: `1.5px dashed ${palette.tokens.hairline}`, borderRadius: 2, bgcolor: 'transparent',
                          cursor: 'pointer', transition: 'background-color 0.15s ease',
                          '&:hover': { bgcolor: palette.tokens.surfaceSoft },
                        }}
                      >
                        <CardContent sx={{ p: OWNER_CARD_SIZES[2].pad, '&:last-child': { pb: OWNER_CARD_SIZES[2].pad }, display: 'flex', alignItems: 'center', gap: OWNER_CARD_SIZES[2].gap }}>
                          <Box sx={{
                            flexShrink: 0, width: OWNER_CARD_SIZES[2].avatar, height: OWNER_CARD_SIZES[2].avatar, borderRadius: '50%',
                            border: `1.5px dashed ${palette.tokens.mutedSoft}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <AddIcon sx={{ fontSize: OWNER_CARD_SIZES[2].avatar * 0.45, color: palette.tokens.mutedSoft }} />
                          </Box>
                          <Typography sx={{ fontWeight: 600, fontSize: OWNER_CARD_SIZES[2].font, color: palette.tokens.muted }}>
                            New profile
                          </Typography>
                        </CardContent>
                      </Card>
                    ) : null}
                  </Box>
                </>
              );
            })()}
          </Box>
        </Box>
      </Box>
      </Box>{/* end top row */}

      {/* ── Portfolio — same heading treatment as DashboardPage.tsx's "Portfolio" section
          header (displaySm serif h3 + hairline rule filling the row), so both trees read as
          one family. ── */}
      <Box sx={{ mb: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="displaySm" component="h3" sx={{ fontSize: '2rem', fontWeight: 700, color: palette.tokens.ink, whiteSpace: 'nowrap' }}>
            Portfolio
          </Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: palette.tokens.hairline }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mt: 0.75 }}>
          <Typography sx={{ color: palette.tokens.mutedSoft, fontSize: '0.82rem' }}>
            Every building and flat you manage
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {hasCertificateProperties && (
              <Button
                size="small"
                startIcon={buildingFromCertificates ? <CircularProgress size={14} /> : <BuildIcon />}
                variant="outlined"
                onClick={handleBuildFromCertificates}
                disabled={buildingFromCertificates}
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12, borderColor: palette.tokens.hairline, color: palette.tokens.muted }}
              >
                {buildingFromCertificates ? 'Building…' : 'Build from my Certificates'}
              </Button>
            )}
            <Button
              size="small"
              startIcon={<MapIcon />}
              variant="outlined"
              onClick={() => setMapDialogOpen(true)}
              sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12, borderColor: palette.tokens.hairline, color: palette.tokens.muted }}
            >
              Show on map{mapMarkers.length > 0 ? ` (${mapMarkers.length})` : ''}
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => openAdd()}
              sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12 }}
            >
              Add property
            </Button>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : properties.length === 0 ? (
        <Paper elevation={0} sx={{
          border: `2px dashed ${palette.tokens.hairline}`, borderRadius: 3, p: 6,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        }}>
          <PropertiesIcon sx={{ fontSize: 48, color: palette.tokens.hairline }} />
          <Typography variant="titleSm" sx={{ color: palette.tokens.muted }}>No properties yet</Typography>
          <Typography variant="bodySm" sx={{ color: palette.tokens.mutedSoft, textAlign: 'center', maxWidth: 360 }}>
            Add your first property to start tracking rent collection and tenant deposits.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAdd()} sx={{ borderRadius: 2 }}>
              Add your first property
            </Button>
            {hasCertificateProperties && (
              <Button
                variant="outlined"
                startIcon={buildingFromCertificates ? <CircularProgress size={16} /> : <BuildIcon />}
                onClick={handleBuildFromCertificates}
                disabled={buildingFromCertificates}
                sx={{ borderRadius: 2, borderColor: palette.tokens.hairline, color: palette.tokens.muted }}
              >
                {buildingFromCertificates ? 'Building…' : 'Build from my Certificates'}
              </Button>
            )}
          </Box>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {properties.map(prop => (
            <BuildingCard
              key={prop.normalized_address}
              prop={prop}
              owners={owners}
              expanded={expandedCards.has(prop.normalized_address)}
              onToggleExpand={() => toggleExpand(prop.normalized_address)}
              onAssignBuildingOwner={handleAssignBuildingOwner}
              onAssignBuildingManager={handleAssignBuildingManager}
              onUpdateStatus={handleUpdateFlatStatus}
              onRenameBuilding={handleRenameBuilding}
              onOpenTenancy={openTenancyDialog}
              onOpenPropertyDetails={openEdit}
              onDeleteFlat={setDeleteTarget}
              onAddFlatToBuilding={openAdd}
              onDeleteBuilding={setDeleteBuildingTarget}
            />
          ))}
        </Box>
      )}

      {/* ── Add / Edit dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editTarget ? 'Property details' : propertyKind === 'whole' ? 'Add property' : (unitType === 'office' ? 'Add office' : 'Add flat')}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>

            <Typography variant="caption" sx={{ color: palette.tokens.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Property details *
            </Typography>
            <TextField
              label="Property name"
              placeholder="e.g. 22 Baker Street, or Riverside Court"
              value={form.normalized_address}
              onChange={e => setForm(f => ({ ...f, normalized_address: e.target.value }))}
              size="small"
              fullWidth
              helperText={editTarget
                ? 'Rename from the building header in the property tree'
                : kindLocked
                  ? 'Adding another flat to this building'
                  : 'A short name for this building — groups flats under it. Find its exact location in Property info below'}
              disabled={!!editTarget || kindLocked}
            />

            {!editTarget && !kindLocked && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <KindOptionCard
                  icon={<HomeIcon sx={{ fontSize: 18 }} />}
                  title="Whole property"
                  selected={propertyKind === 'whole'}
                  onClick={() => selectPropertyKind('whole')}
                />
                <KindOptionCard
                  icon={<ApartmentIcon sx={{ fontSize: 18 }} />}
                  title="Building with flats/offices"
                  selected={propertyKind === 'building'}
                  onClick={() => selectPropertyKind('building')}
                />
              </Box>
            )}

            {editTarget ? (
              <TextField
                label="Flat address"
                placeholder="e.g. 22 Baker Street, Flat 1, London W1"
                value={form.flat_address}
                onChange={e => setForm(f => ({ ...f, flat_address: e.target.value }))}
                size="small"
                fullWidth
              />
            ) : (
              <Tooltip
                title={propertyKind === 'whole' ? "Switch to ‘Building with flats/offices’ above if you want to add a flat or office number" : ''}
                disableHoverListener={propertyKind !== 'whole'}
              >
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ width: 150 }} disabled={propertyKind === 'whole'}>
                    <InputLabel id="unit-type-label">Type</InputLabel>
                    <Select
                      labelId="unit-type-label"
                      label="Type"
                      value={unitType}
                      onChange={e => setUnitType(e.target.value as 'flat' | 'office')}
                    >
                      <MenuItem value="flat">Flat</MenuItem>
                      <MenuItem value="office">Office</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label={unitType === 'office' ? 'Office number' : 'Flat number'}
                    placeholder={unitType === 'office' ? 'e.g. 1, G2' : 'e.g. 1, 2B'}
                    value={unitNumber}
                    onChange={e => setUnitNumber(e.target.value)}
                    size="small"
                    fullWidth
                    disabled={propertyKind === 'whole'}
                  />
                </Box>
              </Tooltip>
            )}

            <FormAccordion title="Property location">
              <AddressFinder
                label="Postcode / zip code"
                placeholder="e.g. W1U 3BW"
                value={locationQuery}
                onInputChange={setLocationQuery}
                onSelect={selectAddressMatch}
                helperText={form.latitude !== null
                  ? `Verified — ${form.formatted_address}`
                  : 'Search a postcode to find this property on the map'}
              />
              {!manualLocationOpen && (
                <Button
                  size="small"
                  onClick={() => setManualLocationOpen(true)}
                  sx={{ alignSelf: 'flex-start', color: palette.tokens.muted, fontSize: 12, textTransform: 'none', px: 0 }}
                >
                  Can't find it? Enter manually
                </Button>
              )}
              <Typography variant="caption" sx={{ color: palette.tokens.muted }}>Country</Typography>
              <CountryField value={form.country} onChange={c => setForm(f => ({ ...f, country: c }))} />
              {manualLocationOpen && (
                <TextField
                  label="District / area"
                  placeholder="e.g. West Kensington"
                  value={form.district}
                  onChange={e => setForm(f => ({ ...f, district: e.target.value }))}
                  size="small"
                  fullWidth
                />
              )}
            </FormAccordion>

            <FormAccordion title="Property info">
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Bedrooms"
                  value={form.bedrooms}
                  onChange={e => setForm(f => ({ ...f, bedrooms: e.target.value }))}
                  size="small"
                  fullWidth
                  inputProps={{ type: 'number', min: 0 }}
                />
                <TextField
                  label="Bathrooms"
                  value={form.bathrooms}
                  onChange={e => setForm(f => ({ ...f, bathrooms: e.target.value }))}
                  size="small"
                  fullWidth
                  inputProps={{ type: 'number', min: 0 }}
                />
              </Box>
            </FormAccordion>

            {/* Rent/rent-due-day are asked here on both Add and Edit — this is where a landlord
                sets or updates what a flat is billed, independent of tenancy. Unit status stays
                Add-only (its own quick-edit pill on the flat panel already owns status changes
                for an existing flat, with active-tenancy-conflict handling built in). */}
            <FormAccordion title={editTarget ? 'Rent' : 'Rent & status'}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Monthly rent (£)"
                  placeholder="1850"
                  value={form.monthly_rent}
                  onChange={e => setForm(f => ({ ...f, monthly_rent: e.target.value }))}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Rent due day"
                  placeholder="1–31"
                  value={form.rent_due_day}
                  onChange={e => setForm(f => ({ ...f, rent_due_day: e.target.value }))}
                  size="small"
                  sx={{ width: 140 }}
                  helperText="Day of month"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ type: 'number', min: 1, max: 31 }}
                />
              </Box>
              {!editTarget && (
                <FormControl size="small" fullWidth>
                  <InputLabel id="unit-status-label">Unit status</InputLabel>
                  <Select
                    labelId="unit-status-label"
                    label="Unit status"
                    value={form.unit_status}
                    onChange={e => setForm(f => ({ ...f, unit_status: e.target.value as UnitStatus }))}
                  >
                    {UNIT_STATUSES.map(s => (
                      <MenuItem key={s} value={s} sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: unitStatusColor(s, palette.tokens), flexShrink: 0 }} />
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_hmo}
                    onChange={e => setForm(f => ({ ...f, is_hmo: e.target.checked }))}
                    size="small"
                  />
                }
                label={<Typography variant="body2">This is a House in Multiple Occupation (HMO) — ask for each tenant's own rent share when starting a tenancy, instead of one lump rent</Typography>}
              />
            </FormAccordion>

            {/* Owner & management is asked here on both Add and Edit now — the standalone
                "Ownership & management" dialog it used to live in was removed (2026-08-13), since
                it saved through this exact same PUT endpoint anyway. Tenant/deposit stay Add-only
                legacy fields — real tenancy/deposit tracking lives in the Tenancy dialog instead. */}
            <FormAccordion title="Owner & management (optional)">
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" fullWidth>
                  <Select
                    value={form.owner_id}
                    displayEmpty
                    onChange={e => setForm(f => ({ ...f, owner_id: e.target.value }))}
                    renderValue={(val) => !val ? 'Owner: none' : `Owner: ${ownerName(val as string) || 'none'}`}
                  >
                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                    {owners.filter(o => o.is_owner).map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <Select
                    value={form.managed_by_id}
                    displayEmpty
                    onChange={e => setForm(f => ({ ...f, managed_by_id: e.target.value }))}
                    renderValue={(val) => !val ? 'Managed by: none' : `Managed by: ${ownerName(val as string) || 'none'}`}
                  >
                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                    {owners.filter(o => o.is_manager).map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>

              {!!form.managed_by_id && form.managed_by_id !== form.owner_id && (
                <Box sx={{ pl: 2, borderLeft: `2px solid ${palette.tokens.hairline}`, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
                    Third-party managed — {selectedFormOwner ? selectedFormOwner.name : 'the owner'} doesn't run this property day to day.
                    Optionally set a management commission:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl size="small" sx={{ width: 160 }}>
                      <Select
                        value={form.management_fee_type}
                        onChange={e => setForm(f => ({ ...f, management_fee_type: e.target.value as 'percent' | 'fixed' }))}
                      >
                        <MenuItem value="percent">% of rent</MenuItem>
                        <MenuItem value="fixed">Fixed amount</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label={form.management_fee_type === 'percent' ? 'Commission (%)' : 'Commission (£/mo)'}
                      placeholder={form.management_fee_type === 'percent' ? 'e.g. 10' : 'e.g. 150'}
                      value={form.management_fee_value}
                      onChange={e => setForm(f => ({ ...f, management_fee_value: e.target.value }))}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </Box>
              )}
            </FormAccordion>

            {!editTarget && (
              <>
                <FormAccordion title="Tenant (optional)">
                  <TextField
                    label="Tenant name"
                    value={form.tenant_name}
                    onChange={e => setForm(f => ({ ...f, tenant_name: e.target.value }))}
                    size="small"
                    fullWidth
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Tenant email"
                      value={form.tenant_email}
                      onChange={e => setForm(f => ({ ...f, tenant_email: e.target.value }))}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Phone"
                      value={form.tenant_phone}
                      onChange={e => setForm(f => ({ ...f, tenant_phone: e.target.value }))}
                      size="small"
                      sx={{ width: 160 }}
                    />
                  </Box>
                </FormAccordion>

                <FormAccordion title="Deposit (optional)">
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label="Deposit amount (£)"
                      value={form.deposit_amount}
                      onChange={e => setForm(f => ({ ...f, deposit_amount: e.target.value }))}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.deposit_lodged}
                          onChange={e => setForm(f => ({ ...f, deposit_lodged: e.target.checked }))}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">DPS lodged</Typography>}
                    />
                  </Box>
                </FormAccordion>
              </>
            )}

            {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.normalized_address.trim() || !resolvedFlatAddress || (!editTarget && propertyKind === 'building' && !unitNumber.trim())}
            sx={{ borderRadius: 2 }}
          >
            {saving ? 'Saving…' : editTarget ? 'Save changes' : propertyKind === 'whole' ? 'Add property' : (unitType === 'office' ? 'Add office' : 'Add flat')}
          </Button>
        </DialogActions>
      </Dialog>

      <PropertiesMapDialog
        open={mapDialogOpen}
        onClose={() => setMapDialogOpen(false)}
        markers={mapMarkers}
      />

      {/* ── Delete flat confirmation ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete flat?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            This will permanently delete <strong>{deleteTarget?.flat_address}</strong> and all its payment history.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete whole property/building confirmation ── */}
      <Dialog open={!!deleteBuildingTarget} onClose={() => setDeleteBuildingTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete property?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            This will permanently delete <strong>{deleteBuildingTarget?.normalized_address}</strong> and{' '}
            {deleteBuildingTarget && deleteBuildingTarget.flat_count > 1
              ? <>all <strong>{deleteBuildingTarget.flat_count} flats</strong> in it</>
              : 'its flat'}, including their payment history. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteBuildingTarget(null)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteBuilding} disabled={deletingBuilding} sx={{ borderRadius: 2 }}>
            {deletingBuilding ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Tenancy dialog (FR-2.2/2.3 — current tenancy + full history per unit) ── */}
      <Dialog open={tenancyDialogOpen} onClose={() => setTenancyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Tenancy</DialogTitle>
        <DialogContent
          dividers
          sx={{
            '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.85rem' },
            '& .MuiInputLabel-root': { fontSize: '0.8rem' },
          }}
        >
          {tenancyHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
                {tenancyTarget?.flat_address}
              </Typography>

              {activeTenancy && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.25, borderRadius: '8px', bgcolor: palette.tokens.surfaceSoft }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: palette.tokens.ink }}>
                    Active tenancy · started {activeTenancy.start_date}
                  </Typography>
                  <Button size="small" onClick={() => { setDeleteTenantsOnEnd(false); setEndTenancyConfirm(true); }} disabled={savingTenancy} sx={{ color: palette.tokens.muted }}>
                    End tenancy
                  </Button>
                </Box>
              )}

              <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: palette.tokens.ink }}>Tenants *</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Autocomplete
                multiple
                size="small"
                options={tenants}
                getOptionLabel={t => t.name}
                filterOptions={(options, params) => {
                  const q = params.inputValue.trim().toLowerCase();
                  const filtered = q ? options.filter(t => t.name.toLowerCase().includes(q)) : options;
                  return [
                    ...filtered,
                    {
                      id: ADD_NEW_TENANT_OPTION_ID,
                      name: params.inputValue.trim() ? `Add "${params.inputValue.trim()}"` : 'Add new tenant',
                      id_reference: null, phone: null, email: null, preferred_contact_channel: null, preferred_language: null,
                    },
                  ];
                }}
                value={tenants.filter(t => tenancyForm.tenant_ids.includes(t.id))}
                onChange={(_e, val) => {
                  const addNew = val.find(t => t.id === ADD_NEW_TENANT_OPTION_ID);
                  setTenancyForm(f => ({ ...f, tenant_ids: val.filter(t => t.id !== ADD_NEW_TENANT_OPTION_ID).map(t => t.id) }));
                  if (addNew) {
                    const typed = addNew.name.match(/^Add "(.*)"$/);
                    setEditingTenantId(null);
                    setNewTenantDraft(d => ({ ...d, name: typed ? typed[1] : '' }));
                    setAddingTenant(true);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, ...(option.id === ADD_NEW_TENANT_OPTION_ID ? { fontWeight: 600, color: palette.tokens.primary } : {}) }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                      {option.id === ADD_NEW_TENANT_OPTION_ID && <AddIcon sx={{ fontSize: 16, mr: 1 }} />}
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{option.name}</Box>
                    </Box>
                    {option.id !== ADD_NEW_TENANT_OPTION_ID && (
                      <IconButton
                        size="small"
                        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                        onClick={e => { e.preventDefault(); e.stopPropagation(); setDeleteTenantError(null); setDeleteTenantTarget(option); }}
                        sx={{ color: palette.tokens.muted, flexShrink: 0 }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} placeholder="Select or add tenant(s)" />}
                renderTags={(value, getTagProps) => value.map((t, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...chipProps}
                      label={t.name}
                      icon={<EditIcon sx={{ fontSize: 14 }} />}
                      onClick={() => openEditTenant(t)}
                    />
                  );
                })}
              />
              {editingTenantId && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, borderRadius: '8px', border: `1px solid ${palette.tokens.hairline}`, bgcolor: palette.tokens.surfaceSoft }}>
                  <Typography variant="caption" sx={{ color: palette.tokens.muted, fontWeight: 700 }}>Editing tenant info</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField label="Name" size="small" fullWidth value={editTenantDraft.name} onChange={e => setEditTenantDraft(d => ({ ...d, name: e.target.value }))} />
                    <TextField label="ID/passport ref" size="small" fullWidth value={editTenantDraft.id_reference} onChange={e => setEditTenantDraft(d => ({ ...d, id_reference: e.target.value }))} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField label="Phone" size="small" fullWidth value={editTenantDraft.phone} onChange={e => setEditTenantDraft(d => ({ ...d, phone: e.target.value }))} />
                    <TextField label="Email" size="small" fullWidth value={editTenantDraft.email} onChange={e => setEditTenantDraft(d => ({ ...d, email: e.target.value }))} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="edit-preferred-contact-label">Preferred contact</InputLabel>
                      <Select
                        labelId="edit-preferred-contact-label"
                        label="Preferred contact"
                        value={editTenantDraft.preferred_contact_channel}
                        onChange={e => setEditTenantDraft(d => ({ ...d, preferred_contact_channel: e.target.value }))}
                      >
                        <MenuItem value=""><em>Not set</em></MenuItem>
                        {TENANT_CONTACT_CHANNELS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField label="Preferred language" size="small" fullWidth value={editTenantDraft.preferred_language} onChange={e => setEditTenantDraft(d => ({ ...d, preferred_language: e.target.value }))} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setEditingTenantId(null)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={handleSaveTenantEdit} disabled={savingTenantEdit || !editTenantDraft.name.trim()}>
                      {savingTenantEdit ? 'Saving…' : 'Save tenant'}
                    </Button>
                  </Box>
                </Box>
              )}
              {addingTenant ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, borderRadius: '8px', border: `1px solid ${palette.tokens.hairline}` }}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField label="Name" size="small" fullWidth value={newTenantDraft.name} onChange={e => setNewTenantDraft(d => ({ ...d, name: e.target.value }))} />
                    <TextField label="ID/passport ref" size="small" fullWidth value={newTenantDraft.id_reference} onChange={e => setNewTenantDraft(d => ({ ...d, id_reference: e.target.value }))} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField label="Phone" size="small" fullWidth value={newTenantDraft.phone} onChange={e => setNewTenantDraft(d => ({ ...d, phone: e.target.value }))} />
                    <TextField label="Email" size="small" fullWidth value={newTenantDraft.email} onChange={e => setNewTenantDraft(d => ({ ...d, email: e.target.value }))} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="preferred-contact-label">Preferred contact</InputLabel>
                      <Select
                        labelId="preferred-contact-label"
                        label="Preferred contact"
                        value={newTenantDraft.preferred_contact_channel}
                        onChange={e => setNewTenantDraft(d => ({ ...d, preferred_contact_channel: e.target.value }))}
                      >
                        <MenuItem value=""><em>Not set</em></MenuItem>
                        {TENANT_CONTACT_CHANNELS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField label="Preferred language" size="small" fullWidth value={newTenantDraft.preferred_language} onChange={e => setNewTenantDraft(d => ({ ...d, preferred_language: e.target.value }))} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setAddingTenant(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={handleCreateAndAddTenant} disabled={creatingTenant || !newTenantDraft.name.trim()}>
                      {creatingTenant ? 'Adding…' : 'Add tenant'}
                    </Button>
                  </Box>
                </Box>
              ) : null}
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters elevation={0} sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: palette.tokens.ink }}>Tenancy terms</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Start date" type="date" size="small" fullWidth
                  value={tenancyForm.start_date}
                  onChange={e => setTenancyForm(f => ({ ...f, start_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End date" type="date" size="small" fullWidth
                  value={tenancyForm.end_date}
                  onChange={e => setTenancyForm(f => ({ ...f, end_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave blank for a rolling/open-ended tenancy"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Notice period (days)" size="small" fullWidth
                  value={tenancyForm.notice_period_days}
                  onChange={e => setTenancyForm(f => ({ ...f, notice_period_days: e.target.value }))}
                  inputProps={{ type: 'number', min: 0 }}
                />
                <TextField
                  label="Notice served on" type="date" size="small" fullWidth
                  value={tenancyForm.notice_served_date}
                  onChange={e => setTenancyForm(f => ({ ...f, notice_served_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  helperText="Sets unit status to Notice given"
                />
              </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters elevation={0} sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: palette.tokens.ink }}>Rent info</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {tenancyTarget?.is_hmo && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, borderRadius: '8px', bgcolor: palette.tokens.surfaceSoft }}>
                  <Typography variant="caption" sx={{ color: palette.tokens.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Rent per tenant — HMO
                  </Typography>
                  {tenancyForm.tenant_ids.length === 0 ? (
                    <Typography variant="body2" sx={{ color: palette.tokens.mutedSoft }}>Add tenants above to set each one's rent.</Typography>
                  ) : (
                    tenancyForm.tenant_ids.map(tid => (
                      <Box key={tid} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="body2" sx={{ flex: 1, color: palette.tokens.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tenants.find(t => t.id === tid)?.name || 'Tenant'}
                        </Typography>
                        <TextField
                          label="Rent (£)" size="small" sx={{ width: 120 }}
                          value={tenancyForm.tenant_rents[tid] || ''}
                          onChange={e => setTenancyForm(f => ({ ...f, tenant_rents: { ...f.tenant_rents, [tid]: e.target.value } }))}
                        />
                      </Box>
                    ))
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, mt: 0.5, borderTop: `1px solid ${palette.tokens.hairline}` }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: palette.tokens.ink }}>Total rent</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: palette.tokens.ink }}>
                      {fmtGBP(Object.values(tenancyForm.tenant_rents).reduce((s, v) => s + (Number(v) || 0), 0))}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {!tenancyTarget?.is_hmo && (
                  <TextField
                    label="Rent amount" size="small" sx={{ width: 110, flexShrink: 0 }}
                    value={tenancyForm.rent_amount}
                    onChange={e => setTenancyForm(f => ({ ...f, rent_amount: e.target.value }))}
                  />
                )}
                <FormControl size="small" sx={{ width: 90, flexShrink: 0 }}>
                  <InputLabel id="currency-label">Currency</InputLabel>
                  <Select
                    labelId="currency-label"
                    label="Currency"
                    value={tenancyForm.currency}
                    onChange={e => setTenancyForm(f => ({ ...f, currency: e.target.value }))}
                  >
                    {TENANCY_CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ width: 130, flexShrink: 0 }}>
                  <InputLabel id="payment-freq-label">Frequency</InputLabel>
                  <Select
                    labelId="payment-freq-label"
                    label="Frequency"
                    value={tenancyForm.payment_frequency}
                    onChange={e => setTenancyForm(f => ({ ...f, payment_frequency: e.target.value as Tenancy['payment_frequency'] }))}
                  >
                    {TENANCY_PAYMENT_FREQUENCIES.map(freq => <MenuItem key={freq} value={freq}>{freq}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id="due-day-label" shrink>Due day</InputLabel>
                  <Select
                    labelId="due-day-label"
                    label="Due day"
                    displayEmpty
                    value={tenancyForm.payment_due_day}
                    onChange={e => setTenancyForm(f => ({ ...f, payment_due_day: e.target.value }))}
                  >
                    <MenuItem value=""><em>Not set</em></MenuItem>
                    {DUE_DAY_OPTIONS.map(d => <MenuItem key={d} value={d.toString()}>{d}</MenuItem>)}
                    <MenuItem value={DUE_DAY_END_OF_MONTH.toString()}>Last day of month</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Deposit amount (£)" size="small" fullWidth
                  value={tenancyForm.deposit_amount}
                  onChange={e => setTenancyForm(f => ({ ...f, deposit_amount: e.target.value }))}
                />
                <FormControl size="small" fullWidth disabled={!tenancyForm.deposit_amount}>
                  <InputLabel id="deposit-status-label">Deposit status</InputLabel>
                  <Select
                    labelId="deposit-status-label"
                    label="Deposit status"
                    value={tenancyForm.deposit_status || 'held'}
                    onChange={e => setTenancyForm(f => ({ ...f, deposit_status: e.target.value as Tenancy['deposit_status'] }))}
                  >
                    {TENANCY_DEPOSIT_STATUSES.map(s => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <TextField
                label="Payment reference (optional)"
                placeholder="e.g. FLAT2B"
                value={paymentReferenceDraft}
                onChange={e => setPaymentReferenceDraft(e.target.value)}
                size="small"
                fullWidth
                helperText="Ask the tenant to use this as their bank transfer reference"
                FormHelperTextProps={{ sx: { fontSize: '0.7rem', fontStyle: 'italic', color: palette.tokens.mutedSoft, mt: 0.4 } }}
              />
                </AccordionDetails>
              </Accordion>

              {tenancyHistory.filter(t => t.status !== 'Active').length > 0 && (
                <>
                  <Divider />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: palette.tokens.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      History
                    </Typography>
                    <Button size="small" onClick={() => setClearHistoryConfirmOpen(true)} sx={{ color: palette.tokens.error, fontSize: 11, fontWeight: 600, minWidth: 0, p: 0 }}>
                      Clear history
                    </Button>
                  </Box>
                  {tenancyHistory.filter(t => t.status !== 'Active').map(t => (
                    <Box key={t.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: '0.8rem', color: palette.tokens.muted }}>
                      <span>{t.tenants.map(tn => tn.name).join(', ') || 'No tenant'} · {t.start_date} → {t.end_date || '—'}</span>
                      <span>{t.display_status}</span>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          )}
          {tenancySaveError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setTenancySaveError(null)}>{tenancySaveError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTenancyDialogOpen(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTenancy}
            disabled={savingTenancy || !tenancyForm.start_date || !tenancyForm.tenant_ids.length || (
              tenancyTarget?.is_hmo
                ? Object.values(tenancyForm.tenant_rents).reduce((s, v) => s + (Number(v) || 0), 0) <= 0
                : !tenancyForm.rent_amount
            )}
            sx={{ borderRadius: 2 }}
          >
            {savingTenancy ? 'Saving…' : activeTenancy ? 'Save changes' : 'Start tenancy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── End tenancy confirmation ── */}
      <Dialog open={endTenancyConfirm} onClose={() => setEndTenancyConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>End this tenancy?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            {activeTenancy?.tenants.map(t => t.name).join(', ') || 'This tenant'} will be marked as ended today, and the unit's status will switch to Vacant.
          </Typography>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={deleteTenantsOnEnd}
                onChange={e => setDeleteTenantsOnEnd(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Also delete this tenant's info (removes them from your reusable tenant list — only if not on another active tenancy)</Typography>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEndTenancyConfirm(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingTenancy}
            onClick={() => handleEndTenancy(deleteTenantsOnEnd)}
            sx={{ borderRadius: 2 }}
          >
            {savingTenancy ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete tenant confirmation (from the "Select or add tenant(s)" dropdown) ── */}
      <Dialog open={!!deleteTenantTarget} onClose={() => { setDeleteTenantTarget(null); setDeleteTenantError(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete {deleteTenantTarget?.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            This permanently removes {deleteTenantTarget?.name} from your reusable tenant list. This can't be undone.
          </Typography>
          {deleteTenantError && (
            <Alert severity="error" sx={{ mt: 2 }}>{deleteTenantError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setDeleteTenantTarget(null); setDeleteTenantError(null); }} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deletingTenant}
            onClick={handleDeleteTenant}
            sx={{ borderRadius: 2 }}
          >
            {deletingTenant ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Status change vs. active tenancy confirmation ── */}
      <Dialog open={!!statusChangeConflict} onClose={() => setStatusChangeConflict(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>This will end the tenancy</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            {statusChangeConflict?.tenantNames.join(', ') || 'This tenant'} has an active tenancy on this unit.
            Changing the status to <strong>{statusChangeConflict?.requestedStatus}</strong> will end it and mark it as terminated today. Are you sure?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setStatusChangeConflict(null)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={resolvingStatusConflict}
            onClick={handleConfirmStatusConflict}
            sx={{ borderRadius: 2 }}
          >
            {resolvingStatusConflict ? 'Saving…' : 'End tenancy & continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Clear tenancy history confirmation ── */}
      <Dialog open={clearHistoryConfirmOpen} onClose={() => setClearHistoryConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Clear tenancy history?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            This permanently deletes every past (Ended/Terminated) tenancy record for <strong>{tenancyTarget?.flat_address}</strong> — this cannot be undone. The current active tenancy, if any, is not affected. Tenant contact records themselves are kept and stay usable elsewhere.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setClearHistoryConfirmOpen(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleClearHistory} disabled={clearingHistory} sx={{ borderRadius: 2 }}>
            {clearingHistory ? 'Clearing…' : 'Clear history'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── All profiles (overflow past the sidebar panel's OWNER_PANEL_CAP cap) ── */}
      <Dialog open={ownersOverflowOpen} onClose={() => setOwnersOverflowOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>All {ownersPanelSource === 'owners' ? 'owners' : 'managers'}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
          {owners.filter(p => ownersPanelSource === 'owners' ? p.is_owner : p.is_manager).map(p => (
            <OwnerCard
              key={p.id}
              owner={p}
              size={1}
              onEdit={() => openEditOwner(p)}
              onDelete={() => setDeleteOwnerTarget(p)}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOwnersOverflowOpen(false)} sx={{ color: palette.tokens.muted }}>Close</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddOwner} sx={{ borderRadius: 2 }}>
            New profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add / Edit profile dialog ── */}
      <Dialog open={ownerDialogOpen} onClose={() => setOwnerDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editOwnerTarget ? 'Edit profile' : 'New profile'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              placeholder="e.g. Katerina, nest&co"
              value={ownerForm.name}
              onChange={e => setOwnerForm(f => ({ ...f, name: e.target.value }))}
              size="small"
              fullWidth
              autoFocus
            />

            <Typography variant="caption" sx={{ color: palette.tokens.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Individual or business? *
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant={ownerForm.entity_type === 'individual' ? 'contained' : 'outlined'}
                onClick={() => setOwnerForm(f => ({ ...f, entity_type: 'individual' }))}
                sx={{ borderRadius: 2, ...(ownerForm.entity_type === 'individual' ? {} : { borderColor: palette.tokens.hairline, color: palette.tokens.muted }) }}
              >
                Individual
              </Button>
              <Button
                fullWidth
                variant={ownerForm.entity_type === 'business' ? 'contained' : 'outlined'}
                onClick={() => setOwnerForm(f => ({ ...f, entity_type: 'business' }))}
                sx={{ borderRadius: 2, ...(ownerForm.entity_type === 'business' ? {} : { borderColor: palette.tokens.hairline, color: palette.tokens.muted }) }}
              >
                Business
              </Button>
            </Box>

            {ownerForm.entity_type === 'business' && (
              <TextField
                label="VAT / tax number (optional)"
                placeholder="e.g. CY12345678X"
                value={ownerForm.vat_number}
                onChange={e => setOwnerForm(f => ({ ...f, vat_number: e.target.value }))}
                size="small"
                fullWidth
              />
            )}

            <Typography variant="caption" sx={{ color: palette.tokens.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Roles *
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControlLabel
                sx={{ flex: 1 }}
                control={
                  <Checkbox
                    checked={ownerForm.is_owner}
                    onChange={e => setOwnerForm(f => ({ ...f, is_owner: e.target.checked }))}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Can be an owner</Typography>}
              />
              <FormControlLabel
                sx={{ flex: 1 }}
                control={
                  <Checkbox
                    checked={ownerForm.is_manager}
                    onChange={e => setOwnerForm(f => ({ ...f, is_manager: e.target.checked }))}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Can be a manager</Typography>}
              />
            </Box>
            {!ownerForm.is_owner && !ownerForm.is_manager && (
              <Typography variant="caption" sx={{ color: palette.tokens.error }}>
                At least one role must be selected.
              </Typography>
            )}

            <Divider />
            <Typography variant="caption" sx={{ color: palette.tokens.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Country (optional)
            </Typography>
            <CountryField value={ownerForm.country} onChange={c => setOwnerForm(f => ({ ...f, country: c }))} />

            <Divider />
            <TextField
              label="Email (optional)"
              placeholder="e.g. katerina@example.com"
              value={ownerForm.email}
              onChange={e => setOwnerForm(f => ({ ...f, email: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Phone (optional)"
              placeholder="e.g. +357 99 123456"
              value={ownerForm.phone}
              onChange={e => setOwnerForm(f => ({ ...f, phone: e.target.value }))}
              size="small"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOwnerDialogOpen(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveOwner}
            disabled={savingOwner || !ownerForm.name.trim() || (!ownerForm.is_owner && !ownerForm.is_manager)}
            sx={{ borderRadius: 2 }}
          >
            {savingOwner ? 'Saving…' : editOwnerTarget ? 'Save changes' : 'Add profile'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete profile confirmation ── */}
      <Dialog open={!!deleteOwnerTarget} onClose={() => setDeleteOwnerTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete "{deleteOwnerTarget?.name}"?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            Any flats assigned to this profile (as owner or manager) will become unassigned. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOwnerTarget(null)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteOwner} sx={{ borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertiesPage;
